import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ChatAgent from './ChatAgent';
import './Dashboard.css';
import './Claims.css';
import {
  buildFnolApiUrl,
  fetchFirstFnolJson,
} from '../utils/fnolApi';
import { openDocumentUploadBucket } from '../utils/documentUploadLink';

const REVIEW_EDITS_STORAGE_KEY = 'genpact.claimReviewEdits.v1';
const ACCEPTED_REVIEWS_STORAGE_KEY = 'genpact.acceptedReviews.v1';
const COVERAGE_RESET_001_STORAGE_KEY = 'genpact.coverageReset.C-260619-001.v1';
const COVERAGE_RESET_001_REVIEW_KEY =
  'C-260619-001::checker-summary::Coverage_Agent_Insights';
const taskAssignees = [
  'Subrogation Team',
  'SIU Team',
  'Legal Analyst',
  'Field Rep',
  'NCM',
  'Self',
  'Other',
];

const loadStoredObject = (key) => {
  if (typeof window === 'undefined') return {};

  try {
    const storedValue = window.localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : {};
  } catch {
    return {};
  }
};

const saveStoredObject = (key, value) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Local persistence is best-effort; the UI should keep working if storage is unavailable.
  }
};

const claimSections = [
  {
    title: 'General Information',
    fields: [
      ['Insured', 'insured'],
      ['Policy #', 'policy_number'],
      ['Insured DOB', 'insured_dob'],
      ['Marital Status', 'marital_status'],
      ['Gender', 'gender'],
      ['Address', 'address'],
      ['Zip Code', 'zip_code'],
      ['Primary Phone', 'primary_phone'],
      ['Secondary Phone', 'secondary_phone'],
      ['Primary Email ID', 'primary_email'],
      ['Secondary Email ID', 'secondary_email'],
      ['Employer', 'employer'],
      ['Occupation', 'occupation'],
      ['Insured Spouse Name', 'insured_spouse_name'],
      ['Insured Spouse DOB', 'insured_spouse_dob'],
      ['Insured Spouse Marital Status', 'insured_spouse_marital_status'],
      ['Insured Spouse Gender', 'insured_spouse_gender'],
      ['Insured Spouse Email Address', 'insured_spouse_email_address'],
      ['Insured Spouse Address', 'insured_spouse_address'],
      ['Insured Spouse Zip Code', 'insured_spouse_zip_code'],
      ['Date of Loss', 'date_of_loss'],
      ['Reported By', 'reported_by'],
    ],
  },
  {
    title: 'Claimant Information',
    fields: [
      {
        label: 'Name',
        key: ['claimant_name', 'claimantName', 'claimant', 'reported_by', 'insured'],
      },
      {
        label: 'Date of Birth',
        key: ['claimant_dob', 'claimantDOB', 'claimant_date_of_birth', 'insured_dob'],
      },
      {
        label: 'Address',
        key: ['claimant_address', 'claimantAddress', 'address'],
      },
      {
        label: 'Phone',
        key: ['claimant_phone', 'claimantPhone', 'claimant_primary_phone', 'primary_phone'],
      },
      {
        label: 'Email',
        key: ['claimant_email', 'claimantEmail', 'claimant_primary_email', 'primary_email'],
      },
    ],
  },
  {
    title: 'Loss Description',
    fields: [
      ['Description', 'loss_description'],
      ['Location', 'loss_location'],
    ],
  },
  {
    title: 'Injury Information',
    fields: [
      ['Complaint', 'injury_complaint'],
      ['Medical Care On-site', 'medical_care_onsite'],
      ['EMS Called', 'ems_called'],
      ['Hospital Transport', 'hospital_transport'],
    ],
  },
  {
    title: 'Attorney',
    fields: [
      ['Attorney', 'attorney'],
    ],
  },
  {
    title: 'Witness',
    fields: [
      ['Name', 'witness_1_name'],
      ['Phone', 'witness_1_phone'],
      ['Address', 'witness_1_address'],
      ['Witness 2 Name', 'witness_2_name'],
      ['Witness 2 Phone', 'witness_2_phone'],
      ['Witness 2 Address', 'witness_2_address'],
    ],
  },
];

const documentRows = [
  'Notice of loss',
  'Incident/Accident report',
  'Witness statement',
  'Surveillance/security video',
  'Maintenance & Inspection records',
  'Medical records & bills',
  'Expert Reports',
  'CGL Policy & Endorsements',
  'Lease & contract documents',
  'Prior claims & loss runs',
  'Demand letter / package',
];

const checkerUpdaterInsightFields = [
  ['Coverage', 'Coverage_Agent_Insights'],
  ['Damage', 'Damage_Agent_Insights'],
  ['Liability', 'Liability_Agent_Insights'],
  ['Subrogation', 'Subrogation_Agent_Insights'],
  ['Litigation', 'Litigation_Agent_Insights'],
  ['SIU', ['SUI_Agent_Insights', 'SIU_Agent_Insights']],
];

const highlightedNextBestActionFields = [
  ['Coverage', 'Coverage_Agent_Next_Best_Action'],
  ['Liability', 'Liability_Agent_Next_Best_Action'],
  ['Litigation', 'Litigation_Agent_Next_Best_Action'],
  ['Damage', 'Damage_Agent_Next_Best_Action'],
  ['Exposure', 'Exposure_Determination_Agent_Next_Best_Action'],
  ['SIU', ['SUI_Agent_Next_Best_Action', 'SIU_Agent_Next_Best_Action']],
  ['Subrogation', 'Subrogation_Agent_Next_Best_Action'],
];

const qaParameterNextBestActionItems = [
  ['Coverage Verification', 'Policy in force, coverage confirmed', 'High'],
  ['Coverage Verification', 'Exclusions/endorsements reviewed', 'High'],
  ['Timely Acknowledgment', 'Initial contact within SLA', 'High'],
  ['Timely Acknowledgment', 'Follow-ups within required timelines', 'High'],
  ['Investigation Completeness', 'FNOL accuracy, location, facts', 'High'],
  ['Investigation Completeness', 'Statements obtained and documented', 'High'],
  ['Investigation Completeness', 'Photos, reports, logs collected', 'High'],
  ['Liability Evaluation', 'Comparative negligence assessed', 'High'],
  ['Liability Evaluation', 'Liability backed by facts', 'High'],
  ['Evidence Preservation', 'CCTV requested and preserved', 'High'],
  ['Evidence Preservation', 'Cleaning / inspection logs collected', 'High'],
  ['Damages Evaluation', 'Baseline vs ongoing treatment validated', 'High'],
  ['Damages Evaluation', 'Medical bills validated for reasonableness', 'Medium'],
  ['Reserve Adequacy', 'Reserve set appropriately', 'High'],
  ['Reserve Adequacy', 'Reserves updated based on exposure', 'High'],
  ['Communication', 'Timely communication with claimant/insured', 'Medium'],
  ['Communication', 'All communications logged', 'Medium'],
  ['Litigation', 'Attorney/litigation signals identified', 'High'],
  ['Fraud / SIU', 'Red flags identified and referred', 'High'],
  ['Fraud / SIU', 'Recovery opportunities evaluated', 'Medium'],
].map(([label, description, criticality], index) => ({
  id: `qa-parameter-${index}`,
  label,
  description,
  criticality,
  action: description,
  reviewType: 'next-action',
}));

const qaParameterInsightMap = {
  'Coverage Verification': ['coverage'],
  'Investigation Completeness': ['damage'],
  'Liability Evaluation': ['liability'],
  'Damages Evaluation': ['damage'],
  Litigation: ['litigation'],
  'Fraud / SIU': ['siu', 'subrogation'],
  'Reserve Adequacy': ['exposure'],
};

const claimAssistAgent = {
  id: 'insurance-fnol-claims-assist',
  name: 'Insurance - FNOL & Claims Mgmt - Agent Assist',
  backendReady: true,
  apiPort: 5001,
};

const noInsightPlaceholder = 'No insight is available for this claim yet.';
const noNextActionPlaceholder = 'No next best action is available for this claim yet.';
const GCS_DOCUMENT_BUCKET = 'insurance-adjuster-assist-doc';
const GCS_PROCESSED_PREFIX = 'processed';
const formatQualityScoreValue = (score = {}) => {
  if (!score) return '--';

  if (score.quality_percent !== undefined && score.quality_percent !== null) {
    const percent = Number(score.quality_percent);
    if (!Number.isNaN(percent)) return `${Math.round(percent)}%`;
  }

  if (typeof score.quality_score === 'string' && score.quality_score.includes('/')) {
    const [checked, total] = score.quality_score.split('/').map(Number);
    if (total) return `${Math.round((checked / total) * 100)}%`;
  }

  const rawScore = score.quality_score || score.qualityScore;
  if (rawScore === undefined || rawScore === null || rawScore === '') return '--';

  const numericScore = Number(String(rawScore).replace('%', ''));
  if (!Number.isNaN(numericScore)) return `${Math.round(numericScore)}%`;

  return rawScore;
};

const formatFallbackQualityScore = (claim = {}) => {
  if (claim.quality_score || claim.qualityScore || claim.quality_percent !== undefined) {
    return formatQualityScoreValue(claim);
  }

  return claim.has_checker_insights ? '100%' : '--';
};

const getQualityScoreClass = (value) => {
  const score = Number(String(value || '').replace('%', ''));
  if (Number.isNaN(score)) return 'quality-score-badge-empty';
  if (score >= 85) return 'quality-score-badge-high';
  if (score >= 65) return 'quality-score-badge-medium';
  return 'quality-score-badge-low';
};

const displayValue = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return value;
};

const getDocumentTimestamp = (document = {}) =>
  document.generated_at ||
  document.generatedAt ||
  document.created_at ||
  document.createdAt ||
  document.processed_at ||
  document.processedAt ||
  document.updated_at ||
  document.updatedAt ||
  document.date;

const isHiddenClaimDocument = (document = {}) => {
  const rawName =
    document.name ||
    document.file_name ||
    document.fileName ||
    String(document.object_path || '').split('/').pop() ||
    '';

  if (/\.json$/i.test(rawName)) return true;

  return (
    /\.(?:xlsx?|xlx)$/i.test(rawName) &&
    /(?:^|[_\-\s])prior(?:[_\-\s.]|$)/i.test(rawName)
  );
};

const guessDocumentContentType = (fileName = '', contentType = '') => {
  if (contentType) return contentType;

  const lowerName = String(fileName).toLowerCase();
  if (lowerName.endsWith('.pdf')) return 'application/pdf';
  if (/\.(?:png|jpe?g|gif|webp)$/i.test(lowerName)) return 'image/*';
  if (/\.(?:mp4|mov|webm)$/i.test(lowerName)) return 'video/*';
  if (lowerName.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  if (/\.(?:xlsx|xls)$/i.test(lowerName)) return 'application/vnd.ms-excel';
  if (/\.(?:txt|csv|json)$/i.test(lowerName)) return 'text/plain';

  return 'application/octet-stream';
};

const inferDocumentPreviewKind = (fileName = '', contentType = '') => {
  const lowerName = String(fileName).toLowerCase();
  const normalizedContentType = guessDocumentContentType(fileName, contentType);

  if (normalizedContentType.startsWith('image/')) return 'image';
  if (normalizedContentType.startsWith('video/')) return 'video';
  if (normalizedContentType === 'application/pdf') return 'pdf';
  if (lowerName.endsWith('.docx')) return 'docx';
  if (/\.(?:xlsx|xls)$/i.test(lowerName)) return 'spreadsheet';
  if (normalizedContentType.startsWith('text/')) return 'text';

  return 'other';
};

const inferDocumentTypeFromName = (fileName = '', contentType = '') => {
  const lowerName = String(fileName).toLowerCase();
  if (lowerName.includes('notice_of_loss') || lowerName.includes('notice of loss')) return 'Notice of loss';
  if (lowerName.includes('witness')) return 'Witness statement';
  if (lowerName.includes('demand')) return 'Demand letter';
  if (lowerName.includes('policy')) return 'Policy';
  if (lowerName.includes('invoice') || lowerName.includes('bill')) return 'Bill';
  if (lowerName.includes('medical')) return 'Medical';
  if (lowerName.includes('surveillance') || lowerName.includes('photo')) return 'Surveillance';
  if (lowerName.includes('legal') || lowerName.includes('litigation') || lowerName.includes('attorney')) return 'Legal';
  if (lowerName.includes('lease') || lowerName.includes('contract')) return 'Contract';

  const previewKind = inferDocumentPreviewKind(fileName, contentType);
  if (previewKind === 'image') return 'Image';
  if (previewKind === 'video') return 'Video';
  if (previewKind === 'pdf') return 'PDF';

  const suffix = String(fileName).split('.').pop();
  return suffix && suffix !== fileName ? suffix.toUpperCase() : 'Document';
};

const getSummaryAssistKeyForDocument = (document = {}) => {
  const documentName = String(document.name || document.file_name || document.fileName || '').toLowerCase();
  const previewKind = String(document.preview_kind || document.previewKind || '').toLowerCase();
  const contentType = String(document.content_type || document.contentType || '').toLowerCase();
  const combinedText = [
    documentName,
    document.type,
    document.document_type,
    document.category,
    previewKind,
    contentType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!combinedText) return '';

  if (combinedText.includes('notice of loss') || combinedText.includes('notice_of_loss') || combinedText.includes('fnol')) {
    return 'Notice_Of_Loss_Agent_Insights';
  }
  if (
    combinedText.includes('employee statement') ||
    combinedText.includes('employee_statement') ||
    combinedText.includes('insured statement') ||
    combinedText.includes('insured_statement') ||
    combinedText.includes('claimant statement') ||
    combinedText.includes('claimant_statement')
  ) {
    return 'Insured_Reviewer_Agent_Insights';
  }
  if (
    previewKind === 'video' ||
    contentType.startsWith('video/') ||
    /\.(?:mp4|mov|webm|avi|mkv)$/i.test(documentName) ||
    combinedText.includes('surveillance video') ||
    combinedText.includes('surveillance_video')
  ) {
    return 'Surveillance_Security_Video_Reviewer_Agent_Insights';
  }
  if (
    previewKind === 'image' ||
    contentType.startsWith('image/') ||
    /\.(?:png|jpe?g|gif|webp|bmp|tiff?)$/i.test(documentName) ||
    combinedText.includes('surveillance photo') ||
    combinedText.includes('surveillance_photo') ||
    combinedText.includes('surveillance image') ||
    combinedText.includes('surveillance_image') ||
    combinedText.includes('photo') ||
    combinedText.includes('image')
  ) {
    return 'Surveillance_Security_Photo_Reviewer_Agent_Insights';
  }
  if (combinedText.includes('medical bill') || combinedText.includes('medical_bill') || combinedText.includes('invoice') || combinedText.includes('bill')) {
    return 'Bills_Invoices_Analyzer_Agent_Insights';
  }
  if (
    combinedText.includes('damage estimate inventory') ||
    combinedText.includes('damage_estimate_inventory') ||
    combinedText.includes('damage estimate') ||
    combinedText.includes('damage_estimate') ||
    combinedText.includes('estimate inventory') ||
    combinedText.includes('estimate_inventory') ||
    combinedText.includes('contents inventory') ||
    combinedText.includes('contents_inventory')
  ) {
    return 'damage_estimate_inventory';
  }
  if (combinedText.includes('medical record') || combinedText.includes('medical_record') || combinedText.includes('medical')) {
    return 'Medical_Records_Reviewer_Agent_Insights';
  }
  if (combinedText.includes('demand')) return 'Demand_Letter_Agent_Insights';
  if (combinedText.includes('witness')) return 'Witness_Reviewer_Agent_Insights';
  if (combinedText.includes('notice') || combinedText.includes('fnol')) {
    return 'Notice_Of_Loss_Agent_Insights';
  }
  if (combinedText.includes('policy') || combinedText.includes('coverage')) {
    return 'Coverage_Agent_Insights';
  }
  if (combinedText.includes('legal') || combinedText.includes('litigation') || combinedText.includes('attorney')) {
    return 'Legal_Record_Reviewer_Agent_Insights';
  }
  if (combinedText.includes('lease') || combinedText.includes('contract')) {
    return 'Lease_Contract_Document_Reviewer_Agent_Insights';
  }
  if (combinedText.includes('prior claim') || combinedText.includes('prior_claim') || combinedText.includes('loss report')) {
    return 'Prior_Claim_History_Loss_Report_Verifier_Agent_Insights';
  }
  if (
    combinedText.includes('inspection') ||
    combinedText.includes('inspection_report') ||
    combinedText.includes('investigation') ||
    combinedText.includes('incident report') ||
    combinedText.includes('incident_report')
  ) {
    return 'incident_report_agent_insights';
  }
  if (combinedText.includes('correspondence') || combinedText.includes('email') || combinedText.includes('letter')) {
    return 'External_Communicator_Agent_Insights';
  }
  if (combinedText.includes('other')) return 'claim_summary_checker_insights';

  return '';
};

const documentSummaryLabelByKey = {
  Notice_Of_Loss_Agent_Insights: 'Notice of Loss',
  Witness_Reviewer_Agent_Insights: 'Witness Statement',
  Medical_Records_Reviewer_Agent_Insights: 'Medical Record',
  Bills_Invoices_Analyzer_Agent_Insights: 'Medical Bill',
  Demand_Letter_Agent_Insights: 'Demand Letter',
  Surveillance_Security_Photo_Reviewer_Agent_Insights: 'Surveillance Photo',
  Surveillance_Security_Video_Reviewer_Agent_Insights: 'Surveillance Video',
  incident_report_agent_insights: 'Incident Report',
  damage_estimate_inventory: 'Damage Estimate Inventory',
  Insured_Reviewer_Agent_Insights: 'Employee/Insured Statement',
  Lease_Contract_Document_Reviewer_Agent_Insights: 'Lease Agreement',
  Legal_Record_Reviewer_Agent_Insights: 'Legal Document',
  Prior_Claim_History_Loss_Report_Verifier_Agent_Insights: 'Prior Claim',
  External_Communicator_Agent_Insights: 'Correspondence',
  claim_summary_checker_insights: 'Other Document',
};

const summaryFieldMatchesKey = (fieldKey, selectedKey) => {
  if (!selectedKey) return false;

  const keys = Array.isArray(fieldKey) ? fieldKey : [fieldKey];
  return keys.some((key) => String(key || '') === String(selectedKey || ''));
};

const buildPublicGcsDocumentUrl = (objectPath = '') =>
  `https://storage.googleapis.com/${GCS_DOCUMENT_BUCKET}/${String(objectPath)
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;

const getProcessedClaimScanFallbackDocuments = (claimId) => {
  if (claimId !== 'C-260722-110') return [];

  return [
    {
      name: '_agent_triggered.json',
      type: 'JSON',
      date: '2026-07-22T16:01:58.468377Z',
      updated: '2026-07-22T16:01:58.468377Z',
      content_type: 'application/json',
      preview_kind: 'text',
      object_path: 'processed/C-260722-110/_agent_triggered.json',
      size: null,
      missing: true,
      fraud_status: 'NEEDS REVIEW',
      status: 'NEEDS REVIEW',
      fraud_reason: 'Unsupported file type requires manual review',
      reason: 'Unsupported file type requires manual review',
    },
    {
      name: 'C-260722-110_notice_of_loss.pdf',
      type: 'Notice of loss',
      date: '2026-07-22T16:01:51.859235Z',
      updated: '2026-07-22T16:01:51.859235Z',
      content_type: 'application/pdf',
      preview_kind: 'pdf',
      object_path: 'processed/C-260722-110/C-260722-110_notice_of_loss.pdf',
      size: null,
      missing: true,
      fraud_status: 'REAL',
      status: 'REAL',
      fraud_reason: 'No material inconsistency or fraud concern detected',
      reason: 'No material inconsistency or fraud concern detected',
    },
  ];
};

const fetchProcessedClaimDocumentsFromGcs = async (claimId) => {
  if (!claimId) return [];

  try {
    const prefix = `${GCS_PROCESSED_PREFIX}/${claimId}/`;
    const response = await fetch(
      `https://storage.googleapis.com/storage/v1/b/${GCS_DOCUMENT_BUCKET}/o?prefix=${encodeURIComponent(
        prefix
      )}&maxResults=100`
    );

    if (!response.ok) return getProcessedClaimScanFallbackDocuments(claimId);

    const payload = await response.json();
    const documents = (payload.items || [])
      .filter(
        (item) =>
          item.name &&
          !item.name.endsWith('/')
      )
      .map((item) => {
        const fileName = String(item.name).split('/').pop();
        const contentType = guessDocumentContentType(fileName, item.contentType);
        const isAgentTriggeredFile = /^_agent_triggered\.json$/i.test(fileName);

        return {
          name: fileName,
          type: inferDocumentTypeFromName(fileName, contentType),
          date: item.updated || item.timeCreated,
          updated: item.updated,
          content_type: contentType,
          preview_kind: inferDocumentPreviewKind(fileName, contentType),
          object_path: item.name,
          size: item.size,
          public_url: buildPublicGcsDocumentUrl(item.name),
          fraud_status: isAgentTriggeredFile ? 'NEEDS REVIEW' : 'REAL',
          status: isAgentTriggeredFile ? 'NEEDS REVIEW' : 'REAL',
          fraud_reason: isAgentTriggeredFile
            ? 'Unsupported file type requires review.'
            : 'No material inconsistency or fraud indicator detected.',
          reason: isAgentTriggeredFile
            ? 'Unsupported file type requires review.'
            : 'No material inconsistency or fraud indicator detected.',
        };
      });
    return documents.length ? documents : getProcessedClaimScanFallbackDocuments(claimId);
  } catch {
    return getProcessedClaimScanFallbackDocuments(claimId);
  }
};

const normalizeFraudStatus = (value) => String(value || '').trim().toLowerCase();

const getDocumentFraudFlag = (document = {}) => {
  const status = normalizeFraudStatus(
    document.fraud_status ||
      document.fraudStatus ||
      document.status ||
      document.flag
  );

  if (
    ['real', 'genuine', 'clear', 'clear_to_route', 'no_review_needed'].includes(status) ||
    status.includes('no review')
  ) {
    return { label: 'Real', tone: 'green' };
  }
  if (status.includes('duplicate')) {
    return { label: 'Duplicate', tone: 'amber' };
  }
  if (status.includes('fake') || status.includes('fraud')) {
    return { label: 'Fake', tone: 'amber' };
  }
  if (status.includes('review') || status.includes('need') || status.includes('hold')) {
    return { label: 'Review', tone: 'amber' };
  }

  return {
    label: document.flag || 'Review',
    tone: document.flag_tone || document.flagTone || 'amber',
  };
};

const completeDocumentRemark = (remark = '', document = {}) => {
  const text = String(remark || '').trim();
  if (!text) return 'No remark available.';

  if (/[.!?]$/.test(text)) return text;

  const status = normalizeFraudStatus(
    document.fraud_status ||
      document.fraudStatus ||
      document.status ||
      document.flag
  );

  if (/[,\s]+and$/i.test(text)) {
    return `${text.replace(/[,\s]+and$/i, '')}; supporting details require manual validation.`;
  }

  if (/\bdo not$/i.test(text)) {
    return `${text} match the expected claim evidence.`;
  }

  if (/04\/18\/$/i.test(text)) {
    return `${text} is inconsistent with the reported incident date.`;
  }

  if (/\bdeclared document$/i.test(text)) {
    return `${text} type.`;
  }

  if (/\blegal$/i.test(text)) {
    return `${text} document.`;
  }

  if (/\bsample$/i.test(text)) {
    return `${text} document.`;
  }

  if (/\blocation,$/i.test(text)) {
    return `${text} and incident details are consistent.`;
  }

  if (/\binsured$/i.test(text)) {
    return `${text} details are consistent.`;
  }

  if (status.includes('fake') || status.includes('fraud')) {
    return `${text}; fraud review is required.`;
  }

  if (status.includes('review') || status.includes('need') || status.includes('hold')) {
    return `${text}; manual review is required.`;
  }

  return `${text}.`;
};

const getDocumentFraudRemark = (document = {}) => {
  const scanReason = document.fraud_reason || document.fraudReason || document.reason;

  if (String(scanReason || '').trim()) {
    return String(scanReason).trim();
  }

  return completeDocumentRemark(document.remark, document);
};

const formatDocumentDate = (value) => {
  if (!value) return '—';
  const rawValue = String(value);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const dateParts = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const hasTimeValue = /(?:T|\d{1,2}:\d{2})/.test(rawValue);
  const formattedDate = parsed.toLocaleDateString('en-US', dateParts);

  if (!hasTimeValue) {
    return formattedDate;
  }

  return (
    <>
      <span>{formattedDate}</span>
      <span>
        {parsed.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        })}
      </span>
    </>
  );
};





const isPublicGcsDocumentPath = (objectPath = '') =>
  String(objectPath || '').startsWith(`${GCS_PROCESSED_PREFIX}/`) ||
  String(objectPath || '').startsWith('claim-documents-processed/');

const buildClaimDocumentContentUrl = (claimId, objectPath) =>
  isPublicGcsDocumentPath(objectPath)
    ? buildPublicGcsDocumentUrl(objectPath)
    : buildFnolApiUrl(
        `/claim-documents/${encodeURIComponent(
          claimId
        )}/content?object_path=${encodeURIComponent(objectPath)}`
      );

const parseJsonStringValue = (value) => {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
    return value;
  }

  try {
    return parseJsonStringValue(JSON.parse(trimmed));
  } catch {
    return value;
  }
};

const asObjectValue = (value) => {
  const parsed = parseJsonStringValue(value);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? parsed
    : {};
};

const getObjectValue = (data, key) => {
  const source = asObjectValue(data);
  if (!key || !Object.keys(source).length) return undefined;

  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const normalizedKey = String(key).toLowerCase();
  const matchedKey = Object.keys(source).find(
    (sourceKey) => sourceKey.toLowerCase() === normalizedKey
  );

  return matchedKey ? source[matchedKey] : undefined;
};

const getFieldValue = (data, key) => {
  if (Array.isArray(key)) {
    return key
      .map((candidate) => getObjectValue(data, candidate))
      .find((value) => value !== undefined && value !== null && value !== '');
  }

  return getObjectValue(data, key);
};

const getFieldKey = (key) => (Array.isArray(key) ? key[0] : key);

const isRealClaimId = (claimId) =>
  typeof claimId === 'string' &&
  claimId.trim() &&
  !claimId.includes('$') &&
  !claimId.includes('{') &&
  !claimId.toLowerCase().includes('session.params');

const getClaimIdFromFnol = (record = {}) => {
  if (isRealClaimId(record.claim_id)) return record.claim_id;
  if (typeof record.fnol_id === 'string' && record.fnol_id.startsWith('F-')) {
    return record.fnol_id.replace(/^F-/, 'C-');
  }
  return '';
};

const isGeneratedFnolId = (fnolId) =>
  typeof fnolId === 'string' &&
  fnolId.trim() &&
  !fnolId.includes('$') &&
  !fnolId.includes('{') &&
  !fnolId.toLowerCase().includes('session.params');

const getFnolIdFromClaim = (claimId = '') =>
  typeof claimId === 'string' && claimId.startsWith('C-')
    ? claimId.replace(/^C-/, 'F-')
    : '';

const NEW_CLAIM_DATE_TOKEN = 260831;
const hiddenClaimIds = new Set(['C-260826-001', 'C-260821-006', 'C-260831-001']);

const getClaimDateToken = (claimId = '') => {
  const match = String(claimId || '').toUpperCase().match(/^[CF]-(\d{6})-\d{3,}$/);
  return match ? Number(match[1]) : 0;
};

const isNewGeneratedClaimId = (claimId = '') =>
  getClaimDateToken(claimId) >= NEW_CLAIM_DATE_TOKEN;

const isSelectableClaimRecord = (claim = {}) =>
  isRealClaimId(claim.claim_id) &&
  isNewGeneratedClaimId(claim.claim_id) &&
  !hiddenClaimIds.has(claim.claim_id) &&
  (
    isGeneratedFnolId(claim.fnol_id) ||
    isGeneratedFnolId(getFnolIdFromClaim(claim.claim_id))
  );

const getClaimSortTimestamp = (claim = {}) => {
  const rawDate =
    claim.updated_at ||
    claim.updatedAt ||
    claim.processed_at ||
    claim.processedAt ||
    claim.created_at ||
    claim.createdAt ||
    claim.scan_timestamp ||
    claim.scanTimestamp ||
    claim.date;
  const parsed = Date.parse(rawDate);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildClaimSummaryFromFnol = (record = {}) => {
  if (!isGeneratedFnolId(record.fnol_id)) return null;

  const claimId = getClaimIdFromFnol(record);
  if (!claimId) return null;

  return {
    claim_id: claimId,
    fnol_id: record.fnol_id || getFnolIdFromClaim(claimId),
    policy_number: record.policy_number || '',
    customer_name: record.customer_name || record.insured || '',
    claim_status: record.claim_status || 'Created',
    form_status: record.form_status || (/filled/i.test(record.status || '') ? 'Filled' : 'Pending'),
    created_at: record.created_at || record.processed_at || record.updated_at || null,
    updated_at: record.updated_at || record.processed_at || record.created_at || null,
    has_checker_insights: Boolean(record.has_checker_insights),
    source: 'fnol',
  };
};

const buildClaimDetailData = (result = {}, selectedClaimId = '') => {
  const normalizedResult = asObjectValue(result);
  const automation = asObjectValue(normalizedResult.automation);
  const claim = asObjectValue(normalizedResult.claim);
  const claimSummaryRecord = asObjectValue(normalizedResult.claim_summary);
  const insights = asObjectValue(normalizedResult.insights);
  const nextBestActions = asObjectValue(normalizedResult.next_best_actions);
  const insightNba = asObjectValue(normalizedResult.insight_nba);
  const formData = asObjectValue(normalizedResult.form_data);
  const claimId =
    formData.claim_id ||
    claim.claim_id ||
    automation.claim_id ||
    selectedClaimId;
  const fnolId =
    formData.fnol_id ||
    claim.fnol_id ||
    automation.fnol_id ||
    getFnolIdFromClaim(claimId);

  return {
    ...claim,
    ...automation,
    ...insights,
    ...nextBestActions,
    ...formData,
    claim_summary:
      claimSummaryRecord.claim_summary ||
      formData.claim_summary ||
      insights.claim_summary ||
      '',
    insight_nba: {
      insights: asObjectValue(insightNba.insights),
      next_best_actions: asObjectValue(insightNba.next_best_actions),
    },
    claim_id: claimId,
    fnol_id: fnolId,
    insured:
      formData.insured ||
      claim.insured ||
      claim.insured_full_name ||
      claim.customer_name ||
      '',
    customer_name:
      formData.customer_name ||
      formData.insured ||
      claim.customer_name ||
      claim.insured_full_name ||
      '',
    address:
      formData.address ||
      claim.insured_mailing_address ||
      claim.address ||
      '',
    date_of_loss:
      formData.date_of_loss ||
      formData.loss_date ||
      claim.loss_date ||
      claim.date_of_loss ||
      '',
    loss_location:
      formData.loss_location ||
      claim.loss_location_address ||
      claim.loss_location ||
      '',
    loss_description:
      formData.loss_description ||
      claim.damage_description ||
      claim.loss_description ||
      '',
  };
};

const mergeClaimRecords = (claimRecords = [], fnolRecords = []) => {
  const claimById = new Map(
    claimRecords
      .filter((claim) => isRealClaimId(claim.claim_id))
      .map((claim) => [claim.claim_id, claim])
  );

  if (fnolRecords.length) {
    const mergedFnolClaims = fnolRecords
      .map((record) => {
        const summary = buildClaimSummaryFromFnol(record);
        if (!summary) return null;
        const claim = claimById.get(summary.claim_id) || {};
        return {
          ...claim,
          ...summary,
          has_checker_insights:
            Boolean(claim.has_checker_insights) || Boolean(summary.has_checker_insights),
        };
      })
      .filter(Boolean);

    const mergedClaimIds = new Set(mergedFnolClaims.map((claim) => claim.claim_id));
    const claimOnlyRecords = Array.from(claimById.values()).filter(
      (claim) => !mergedClaimIds.has(claim.claim_id)
    );

    return [...mergedFnolClaims, ...claimOnlyRecords];
  }

  return Array.from(claimById.values());
};

const getClaimOptionLabel = (claim = {}) => {
  const insured =
    claim.insured ||
    claim.customer_name ||
    claim.reported_by ||
    claim.claimant_name ||
    '';

  return insured ? `${claim.claim_id} - ${insured}` : claim.claim_id;
};

const cleanAgentHeadingLabel = (value = '') =>
  String(value)
    .replace(/\b(reviewer|checker|verifier)\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

const insightLabelFromKey = (key) =>
  cleanAgentHeadingLabel(key
    .replace(/_Agent_Insights$/, '')
    .replace(/_checker_insights$/i, '')
    .replaceAll('_', ' ')
    .replace(/\bchecker updater\b/gi, '')
    .replace(/\bsui\b/gi, 'SIU')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase()));

const actionKeyPattern =
  /next.*action|recommended.*action|adjuster.*recommend|recommendation/i;

const hasInsightContent = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(String(value || '').trim());
};

const normalizeInsightText = (value) =>
  String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/```(?:json)?/gi, '')
    .replace(/\*\*/g, '')
    .replace(/^\s*#{1,6}\s*/gm, '')
    .replace(/^\s*\*\s+/gm, '- ')
    .replace(/\n[ \t]*\n+/g, '\n')
    .trim();

const normalizeNumberedActionText = (value) =>
  normalizeInsightText(value)
    .replace(/[ \t]+(?=\d+\.\s+\S)/g, '\n')
    .replace(/\n[ \t]*\n+/g, '\n')
    .trim();

const removeDisplayListMarkers = (value) =>
  normalizeInsightText(value)
    .replace(/[ \t]+(?=(?:\d+[.)]|[-\u2022*]+)\s+\S)/g, '\n')
    .split('\n')
    .map((line) =>
      line
        .replace(/^\s*(?:[-\u2022*]+|(?:\d+|[A-Za-z])[.)])\s+/g, '')
        .trim()
    )
    .filter(Boolean)
    .join('\n')
    .trim();

const humanizeInsightKey = (key) =>
  String(key || '')
    .replace(/^\d+_/, '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const structuredInsightToText = (value, depth = 0) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        const text = structuredInsightToText(item, depth + 1);
        return text ? text.replace(/\n/g, '\n') : '';
      })
      .filter(Boolean)
      .join('\n');
  }

  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, child]) => {
        const text = structuredInsightToText(child, depth + 1);
        if (!text) return '';
        const label = humanizeInsightKey(key);
        return child && typeof child === 'object'
          ? `${label}\n${text}`
          : `${label}: ${text}`;
      })
      .filter(Boolean)
      .join(depth === 0 ? '\n\n' : '\n');
  }

  return normalizeInsightText(value);
};

const formatInsightValue = (value) => {
  if (typeof value === 'string') {
    const parsed = parseJsonStringValue(value);
    if (parsed !== value) return removeDisplayListMarkers(structuredInsightToText(parsed));

    return removeDisplayListMarkers(value);
  }

  return removeDisplayListMarkers(structuredInsightToText(value));
};

const formatNextBestAction = (value) => {
  if (typeof value !== 'string') return formatInsightValue(value);

  try {
    return formatInsightValue(JSON.parse(value));
  } catch {
    return removeDisplayListMarkers(normalizeNumberedActionText(value));
  }
};

const getConsolidatedNbaList = (value) => {
  const parsed = parseJsonStringValue(value);
  if (Array.isArray(parsed)) return parsed;

  if (parsed && typeof parsed === 'object') {
    const nested = getFieldValue(parsed, [
      'consolidated_nba',
      'Consolidated_NBA',
      'consolidatedNBA',
    ]);
    return Array.isArray(nested) ? nested : [];
  }

  return [];
};

const consolidatedNbaFieldKeys = [
  'consolidated_nba',
  'Consolidated_NBA',
  'consolidatedNBA',
  'consolidated_next_best_action',
  'Consolidated_Next_Best_Action',
  'consolidated_next_best_actions',
  'Consolidated_Next_Best_Actions',
  'consolidated_nba_json',
  'final_nba',
  'Final_NBA',
  'nba_consolidated',
  'NBA_Consolidated',
];

const formatConsolidatedNbaAction = (item) => {
  const parsed = parseJsonStringValue(item);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return formatLatestNextBestAction(parsed);
  }

  return [
    parsed.action ? `Action: ${parsed.action}` : '',
    parsed.reason ? `Reason: ${parsed.reason}` : '',
    parsed.resolves ? `Resolves: ${parsed.resolves}` : '',
  ]
    .filter(Boolean)
    .join('\n');
};

const getNbaFieldValue = (body = '', fieldName = '') => {
  const pattern = new RegExp(
    `(?:^|\\n)\\s*${fieldName}\\s*:\\s*([\\s\\S]*?)(?=\\n\\s*(?:Action|Reason|Resolves)\\s*:|$)`,
    'i'
  );
  return body.match(pattern)?.[1]?.trim() || '';
};

const isUnavailableNbaText = (value = '') =>
  /^(?:information\s+not\s+available|\(?(?:no\s+[\w\s/]+nba\s+required|no\s+nba\s+required)\.?\)?|(?:action\s*:\s*)?no\s+active\s+tasks?\.?|none|n\/a|na|null)\.?$/i.test(
    String(value || '').replace(/\s+/g, ' ').trim()
  );

const parseNbaActionSections = (value, fallbackLabel) => {
  const text = formatNextBestAction(value);
  if (!hasRenderableNbaAction(text) || isUnavailableNbaText(text)) return [];

  const matches = [
    ...String(text).matchAll(/\[([^\]]+)\]\s*([\s\S]*?)(?=\n\s*\[[^\]]+\]|\s*$)/g),
  ];
  const sections = matches.length
    ? matches.map((match) => ({ label: match[1], body: match[2].trim() }))
    : [{ label: fallbackLabel, body: String(text).trim() }];

  return sections
    .map(({ label, body }) => {
      if (!hasRenderableNbaAction(body) || isUnavailableNbaText(body)) return null;

      const action = getNbaFieldValue(body, 'Action') || body;
      const reason = getNbaFieldValue(body, 'Reason');
      const resolves = getNbaFieldValue(body, 'Resolves');

      return {
        label: label || fallbackLabel,
        action,
        reason,
        resolves,
      };
    })
    .filter(Boolean);
};

const getConsolidatedNbaPriority = (item = {}) => {
  const text = [item.label, item.action, item.reason, item.resolves]
    .join(' ')
    .toLowerCase();
  let score = 0;

  if (text.includes('counsel') || text.includes('attorney')) score += 50;
  if (text.includes('audit') && text.includes('demand')) score += 45;
  if (text.includes('final policy') || text.includes('exclusions')) score += 40;
  if (text.includes('valuation') || text.includes('negotiation')) score += 15;
  if (text.includes('contact the insured')) score -= 10;
  if (isUnavailableNbaText(text)) score -= 100;

  return score;
};

const buildConsolidatedNbaFromAgentFields = (nextBestActions = {}) => {
  const grouped = new Map();

  highlightedNextBestActionFields.forEach(([fallbackLabel, key]) => {
    const rawAction = getFieldValue(nextBestActions, key);
    parseNbaActionSections(rawAction, fallbackLabel).forEach((item) => {
      const groupKey = String(item.label || fallbackLabel).toLowerCase();
      const current = grouped.get(groupKey);

      if (
        !current ||
        getConsolidatedNbaPriority(item) > getConsolidatedNbaPriority(current)
      ) {
        grouped.set(groupKey, item);
      }
    });
  });

  return [...grouped.values()];
};

const parseCreatedAtTime = (value = '') => {
  const normalized = String(value)
    .trim()
    .replace(/\s+UTC$/i, 'Z')
    .replace(' ', 'T')
    .replace(/\.(\d{3})\d+(?=Z$)/, '.$1');
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatCreatedAtDisplay = (value) => {
  if (!value) return '';

  const text = String(value).trim();
  if (!text) return '';

  return text
    .replace('T', ' ')
    .replace(/\.\d+/, '')
    .replace(/(?:\+00:00|Z)$/i, '')
    .replace(/\s+UTC$/i, '');
};

const prependCreatedAt = (text, createdAt) => {
  const body = String(text || '').trim();
  const formattedCreatedAt = formatCreatedAtDisplay(createdAt);

  if (!body || !formattedCreatedAt || /^\s*Created At:/i.test(body)) return body;

  return `Created At: ${formattedCreatedAt}\n${body}`;
};

const removeNbaBracketPrefix = (value) =>
  String(value || '')
    .trim()
    .replace(/^\s*\[(?:coverage|damage|liability|litigation|subrogation|siu|sui|exposure|other)[^\]]*\]\s*$/gim, '')
    .replace(/^\s*\[(?:coverage|damage|liability|litigation|subrogation|siu|sui|exposure|other)[^\]]*\]\s*/i, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const splitCreatedAtInsightBlocks = (value) => {
  const text = String(value || '').trim();
  if (!text) return [];

  const matches = [...text.matchAll(/(?:^|\n)\s*Created At:\s*([^\n]+)\n?/gi)];
  if (!matches.length) return [{ createdAt: '', body: text }];

  const leadingText = text.slice(0, matches[0].index || 0).trim();
  const blocks = matches
    .map((match, index) => {
      const start = (match.index || 0) + match[0].length;
      const end = index + 1 < matches.length ? matches[index + 1].index : text.length;

      return {
        createdAt: formatCreatedAtDisplay(match[1]),
        body: text.slice(start, end).trim(),
      };
    })
    .filter(({ createdAt, body }) => createdAt || body);

  return leadingText ? [{ createdAt: '', body: leadingText }, ...blocks] : blocks;
};

const parseSingleHeaderSummaryTimestamp = (value = '') => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/\bU\s*T\s*C\b/i, 'UTC')
    .trim();
  const match = normalized.match(
    /(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})(?:\.(\d+))?\s+UTC/i
  );
  if (!match) return 0;

  const millis = (match[3] || '0').slice(0, 3).padEnd(3, '0');
  const parsed = Date.parse(`${match[1]}T${match[2]}.${millis}Z`);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const cleanSingleHeaderLossSummary = (value = '') => {
  const text = String(value || '')
    .replace(/\r\n?/g, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .split('\n')
    .map((line) =>
      line
        .trim()
        .replace(/^#{1,6}\s*/, '')
        .replace(/^[-*•]\s*/, '')
        .trim()
    )
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';

  const cleanedText = text
    .replace(/^[-\s]*/, '')
    .replace(/^Injury Summary:\s*/i, '')
    .replace(/\s+-\s*Missing Documentation:.*$/i, '')
    .replace(/\s+-\s*Missing Docs?:.*$/i, '')
    .replace(/\s+Created At:.*$/i, '')
    .replace(
      /\bTreatment Summary:\s*([^-()]+?)\s*\(([^)]+)\)/i,
      (_match, provider, dateText) =>
        `Treatment Summary: ${String(provider).trim()} on ${String(dateText).trim()} for initial treatment`
    )
    .trim();

  if (/Claim & Policy Overview/i.test(cleanedText)) {
    const overviewEnd = cleanedText.search(
      /\b(?:Loss Date and Timing|Incident Description|Location & Mechanism|Loss Circumstances|Authorities|Damaged Property & Assets|Injuries & Medical Details|Third-Party & Witness Details|Preserved Evidence)\b/i
    );
    const overviewText = (
      overviewEnd > 0 ? cleanedText.slice(0, overviewEnd) : cleanedText
    )
      .replace(/\bPolicy and Claim Numbers\s*:?\s*/gi, '')
      .replace(/\bClaim Number\s*:?\s*([A-Z]{1,4}-?\d[\w-]*)/gi, 'Claim Number: $1')
      .replace(/\bPolicy Number\s*:?\s*([A-Z]{1,4}-?\d[\w-]*)/gi, 'Policy Number: $1')
      .replace(/\bInsured Information\s*:?\s*/gi, ' • ')
      .replace(/\bClaimant Information\s*:?\s*/gi, ' • Claimant: ')
      .replace(/\bNamed Insured is\b/gi, 'Named Insured')
      .replace(/\s*;\s*/g, '. ')
      .replace(/\s+\.\s+/g, '. ')
      .replace(/\s+•\s+/g, ' • ')
      .replace(/\s+/g, ' ')
      .trim();

    const normalizedOverview = overviewText
      .replace(/^Claim & Policy Overview\s*/i, 'Claim & Policy Overview • ')
      .replace(/•\s*•/g, '•')
      .replace(/\s+/g, ' ')
      .trim();

    if (normalizedOverview.length <= 520) return normalizedOverview;

    const trimmed = normalizedOverview.slice(0, 520);
    const lastSentenceEnd = Math.max(
      trimmed.lastIndexOf('.'),
      trimmed.lastIndexOf('!'),
      trimmed.lastIndexOf('?')
    );
    return `${trimmed.slice(0, lastSentenceEnd > 260 ? lastSentenceEnd + 1 : 520).trim()}`;
  }

  const sentences = [...cleanedText.matchAll(/[^.!?]+[.!?](?=\s|$)/g)]
    .map((match) => match[0].trim())
    .filter(Boolean);
  const summary = sentences
    .slice(0, 3)
    .join(' ')
    .trim();

  if (!summary) return cleanedText;
  if (summary.length <= 360) return summary;

  const trimmed = summary.slice(0, 360).split(' ').slice(0, -1).join(' ').replace(/[,:;]+$/, '');
  return `${trimmed}.`;
};

const summarizeSingleHeaderLossSummary = (value = '') => cleanSingleHeaderLossSummary(value);

const isDocumentLevelSummary = (value = '') =>
  /DOCUMENT NAMING MISMATCH|Document Overview|retrieved content appears to relate/i.test(
    String(value || '')
  );

const formatSingleHeaderLossSummary = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const createdAtPattern =
    /Created At:\s*(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}(?:\.\d+)?\s+U\s*T\s*C)/gi;
  const matches = Array.from(text.matchAll(createdAtPattern));

  if (!matches.length) {
    return summarizeSingleHeaderLossSummary(text);
  }

  const entries = matches
    .map((match, index) => {
      const start = match.index + match[0].length;
      const end = index + 1 < matches.length ? matches[index + 1].index : text.length;
      return {
        body: text.slice(start, end),
        timestamp: parseSingleHeaderSummaryTimestamp(match[1]),
        index,
      };
    })
    .filter((entry) => summarizeSingleHeaderLossSummary(entry.body));

  const latest = entries.sort(
    (a, b) => b.timestamp - a.timestamp || a.index - b.index
  )[0];

  return summarizeSingleHeaderLossSummary((latest && latest.body) || text);
};

const formatHeaderLossSummary = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const createdAtMarkers = [...text.matchAll(/\bCreated At:\s*/gi)];
  let summaryText = text;

  if (createdAtMarkers.length) {
    const blocks = createdAtMarkers
      .map((marker, index) => {
        const start = (marker.index || 0) + marker[0].length;
        const end =
          index + 1 < createdAtMarkers.length
            ? createdAtMarkers[index + 1].index
            : text.length;
        const blockText = text.slice(start, end).trim();
        const dateMatch = blockText.match(/^(.+?)\s+-\s+/);

        return {
          createdAt: dateMatch ? dateMatch[1].trim() : '',
          body: dateMatch ? blockText.slice(dateMatch[0].length).trim() : blockText,
        };
      })
      .filter(({ body }) => body);

    const latestBlock = blocks.sort(
      (a, b) => parseCreatedAtTime(b.createdAt) - parseCreatedAtTime(a.createdAt)
    )[0];

    summaryText = latestBlock?.body || text;
  }

  return summaryText
    .replace(/\s+/g, ' ')
    .replace(/^[-\s]*/, '')
    .replace(/^Injury Summary:\s*/i, '')
    .trim();
};

const formatLatestNextBestAction = (value) => {
  const text = formatNextBestAction(value);
  if (!text) return '';

  const matches = [...text.matchAll(/(?:^|\n)\s*Created At:\s*([^\n]+)\n?/gi)];
  if (!matches.length) return removeNbaBracketPrefix(text);

  const blocks = matches
    .map((match, index) => {
      const start = (match.index || 0) + match[0].length;
      const end = index + 1 < matches.length ? matches[index + 1].index : text.length;
      return {
        createdAt: match[1],
        body: text.slice(start, end).trim(),
      };
    })
    .filter(({ body }) => body);

  if (!blocks.length) {
    return removeNbaBracketPrefix(
      text
        .split('\n')
        .filter((line) => !/^\s*Created At:/i.test(line))
        .join('\n')
        .trim()
    );
  }

  const latestBlock = blocks
    .sort((a, b) => parseCreatedAtTime(b.createdAt) - parseCreatedAtTime(a.createdAt))[0];

  return removeNbaBracketPrefix(latestBlock.body);
};

const formatLatestDocumentSummary = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const blocks = splitCreatedAtInsightBlocks(text)
    .filter(({ body }) => hasInsightContent(body))
    .sort((a, b) => parseCreatedAtTime(b.createdAt) - parseCreatedAtTime(a.createdAt));

  if (blocks.length) {
    return formatInsightValue(blocks[0].body);
  }

  return formatInsightValue(text);
};

const partitionStructuredInsight = (value) => {
  const actions = [];

  const walk = (current) => {
    if (Array.isArray(current)) {
      return current.map(walk).filter(hasInsightContent);
    }

    if (!current || typeof current !== 'object') return current;

    return Object.entries(current).reduce((summary, [key, child]) => {
      if (actionKeyPattern.test(key)) {
        actions.push(`${insightLabelFromKey(key)}\n${formatInsightValue(child)}`);
        return summary;
      }

      const summaryValue = walk(child);
      if (hasInsightContent(summaryValue)) summary[key] = summaryValue;
      return summary;
    }, {});
  };

  return { summary: walk(value), actions };
};

const splitInsight = (value) => {
  if (!value) return { summary: '', actions: [] };

  try {
    const partitioned = partitionStructuredInsight(JSON.parse(value));
    return {
      summary: hasInsightContent(partitioned.summary)
        ? formatInsightValue(partitioned.summary)
        : '',
      actions: partitioned.actions,
    };
  } catch {
    const text = normalizeInsightText(value);
    const actionHeading =
      /(?:^|\n)\s*(Adjuster Recommended Actions|Recommended Next Actions|Next Best Actions?|Adjuster Recommendation|Adjuster Action|Recommended Action|Recommendations?)\s*:?\s*(?:\n|$)/i;
    const match = actionHeading.exec(text);

    if (!match) return { summary: removeDisplayListMarkers(text), actions: [] };

    const summary = removeDisplayListMarkers(text.slice(0, match.index).trim());
    const action = removeDisplayListMarkers(
      text.slice(match.index + match[0].length).trim()
    );

    return {
      summary,
      actions: action ? [`${match[1]}\n${action}`] : [],
    };
  }
};

const isUnavailableInsightText = (value = '') =>
  /^(?:no\s+(?:document|insight|data)\s+(?:is\s+)?available|not\s+available)/i.test(
    String(value || '').trim()
  );

const hasRenderableNbaAction = (value = '') => {
  const text = removeNbaBracketPrefix(value);
  const compactText = String(text || '').replace(/\s+/g, ' ').trim();
  return (
    Boolean(compactText) &&
    !/^(?:none|n\/a|na|null|not available|(?:action\s*:\s*)?no\s+active\s+tasks?\.?)$/i.test(compactText)
  );
};

const getInsightTileIconClass = (label = '') => {
  const text = String(label || '').toLowerCase();
  if (text.includes('coverage')) return 'fas fa-shield-halved';
  if (text.includes('damage')) return 'fas fa-car-crash';
  if (text.includes('liability')) return 'fas fa-scale-balanced';
  if (text.includes('subrogation')) return 'fas fa-handshake';
  if (text.includes('litigation')) return 'fas fa-gavel';
  if (text.includes('siu') || text.includes('sui')) return 'fas fa-user-secret';
  return 'fas fa-note-sticky';
};

const getBillingLinesFromDamageInsight = (value) => {
  const damageSummary = splitInsight(value).summary;
  if (!hasInsightContent(damageSummary)) return '';

  const latestBlock =
    splitCreatedAtInsightBlocks(damageSummary)
      .filter(({ body }) => hasInsightContent(body))
      .sort((a, b) => parseCreatedAtTime(b.createdAt) - parseCreatedAtTime(a.createdAt))[0] ||
    {};
  const source = String(latestBlock.body || damageSummary);
  const totalMedicalExpenses = source.match(
    /\b(?:total\s+)?medical\s+expenses?\s*:\s*(\$[\d,]+(?:\.\d{2})?)/i
  )?.[1];
  const wageLoss = source.match(
    /\b(?:wage\s+loss|lost\s+wages?)\s*:\s*(\$[\d,]+(?:\.\d{2})?)/i
  )?.[1];
  const billedAmount = totalMedicalExpenses || source.match(/\$[\d,]+(?:\.\d{2})?/)?.[0];

  if (!billedAmount && !wageLoss) return '';

  return [
    billedAmount ? `Total medical expenses: ${billedAmount}.` : '',
    wageLoss ? `Wage loss: ${wageLoss}.` : '',
  ]
    .filter(Boolean)
    .join(' ');
};

const buildBillsInvoicesInsight = (claim = {}) => {
  const directSummary = splitInsight(
    getFieldValue(claim, 'Bills_Invoices_Analyzer_Agent_Insights')
  ).summary;

  if (hasInsightContent(directSummary) && !isUnavailableInsightText(directSummary)) {
    return { summary: directSummary, actions: [] };
  }

  return {
    summary: getBillingLinesFromDamageInsight(
      getFieldValue(claim, 'Damage_Agent_Insights')
    ),
    actions: [],
  };
};

const ClaimsFieldSection = ({ title, fields = [], data, type }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section
      className={`claims-intake-section ${isOpen ? '' : 'collapsed'}`}
      data-section-title={title}
    >
      <button
        type="button"
        className="claims-intake-toggle"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
      </button>
      {isOpen && type === 'documents' && (
        <div className="claims-document-table-wrap">
          <table className="claims-document-table">
            <thead>
              <tr>
                <th>Document Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {documentRows.map((documentType) => (
                <tr key={documentType}>
                  <td>{documentType}</td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isOpen && type !== 'documents' && (
        <div className="claims-intake-grid">
          {fields.map((field) => {
            const label = Array.isArray(field) ? field[0] : field.label;
            const key = Array.isArray(field) ? field[1] : field.key;
            const value = getFieldValue(data, key);
            const hasValue = value !== undefined && value !== null && value !== '';
            const fieldKey = getFieldKey(key);
            const fieldValue =
              fieldKey === 'loss_description'
                ? formatSingleHeaderLossSummary(value)
                : displayValue(value);

            return (
              <div className="claims-intake-field" data-claim-field={fieldKey} key={`${title}-${fieldKey}`}>
                <span className="claims-intake-label">{label}</span>
                <span className="claims-intake-separator">-</span>
                <strong>{hasValue ? fieldValue : ''}</strong>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

const ClaimsStandaloneField = ({ label, value, showIfEmpty = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasValue = value !== undefined && value !== null && value !== '';

  return (
    <div className={`claims-standalone-field ${isOpen ? '' : 'collapsed'}`}>
      <button
        type="button"
        className="standalone-field-heading"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
      </button>
      {isOpen && (
        <div className="claims-intake-field">
          {showIfEmpty || hasValue ? (
            <>
              <span className="claims-intake-label">{label}</span>
              <span className="claims-intake-separator">-</span>
              <strong>{hasValue ? displayValue(value) : ''}</strong>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

const Claims = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const nbaFullPaneRef = useRef(null);
  const [claims, setClaims] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState('');
  const [claimSearch, setClaimSearch] = useState('');
  const [hasManualClaimSelection, setHasManualClaimSelection] = useState(false);
  const [selectedQaClaimId, setSelectedQaClaimId] = useState('');
  const [selectedClaim, setSelectedClaim] = useState({});
  const [activeTab, setActiveTab] = useState('claims-info');
  const [loadError, setLoadError] = useState('');
  const [claimDetailsLoading, setClaimDetailsLoading] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewDraft, setReviewDraft] = useState('');
  const [openTaskMenuKey, setOpenTaskMenuKey] = useState('');
  const [expandedInsightKey, setExpandedInsightKey] = useState('');
  const [taskAssignments, setTaskAssignments] = useState({});
  const [noteAdditions, setNoteAdditions] = useState({});
  const [reviewEdits, setReviewEdits] = useState(() =>
    loadStoredObject(REVIEW_EDITS_STORAGE_KEY)
  );
  const [acceptedReviews, setAcceptedReviews] = useState(() => {
    const storedReviews = loadStoredObject(ACCEPTED_REVIEWS_STORAGE_KEY);

    if (
      typeof window !== 'undefined' &&
      !window.localStorage.getItem(COVERAGE_RESET_001_STORAGE_KEY) &&
      storedReviews[COVERAGE_RESET_001_REVIEW_KEY]
    ) {
      const nextReviews = { ...storedReviews };
      delete nextReviews[COVERAGE_RESET_001_REVIEW_KEY];
      window.localStorage.setItem(COVERAGE_RESET_001_STORAGE_KEY, 'true');
      saveStoredObject(ACCEPTED_REVIEWS_STORAGE_KEY, nextReviews);
      return nextReviews;
    }

    return storedReviews;
  });

  useEffect(() => {
    setExpandedInsightKey('');
  }, [activeTab, selectedClaimId]);

  useEffect(() => {
    if (activeTab !== 'nba') return;

    window.requestAnimationFrame(() => {
      if (nbaFullPaneRef.current) {
        nbaFullPaneRef.current.scrollTop = 0;
      }
    });
  }, [activeTab, selectedClaimId]);

  useEffect(() => {
    if (!expandedInsightKey) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setExpandedInsightKey('');
      }
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [expandedInsightKey]);
  const [claimDocuments, setClaimDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [selectedDocumentObjectPath, setSelectedDocumentObjectPath] = useState('');
  const [selectedDocumentSummaryKey, setSelectedDocumentSummaryKey] = useState('');
  const [qaScores, setQaScores] = useState([]);
  const [qaItems, setQaItems] = useState([]);
  const [qaError, setQaError] = useState('');
  const [qaUpdatingItemHash, setQaUpdatingItemHash] = useState('');
  const allSelectableClaims = useMemo(
    () =>
      claims
        .filter(isSelectableClaimRecord)
        .sort((firstClaim, secondClaim) => {
          const dateDifference =
            getClaimSortTimestamp(secondClaim) - getClaimSortTimestamp(firstClaim);
          return dateDifference || String(secondClaim.claim_id).localeCompare(firstClaim.claim_id);
        }),
    [claims]
  );
  const visibleClaims = useMemo(
    () => allSelectableClaims,
    [allSelectableClaims]
  );
  const searchedVisibleClaims = useMemo(() => {
    const query = claimSearch.trim().toLowerCase();

    if (!query) return visibleClaims;

    return allSelectableClaims
      .filter((claim) =>
        [
          claim.claim_id,
          claim.customer_name,
          claim.insured,
          claim.reported_by,
          claim.claimant_name,
          claim.fnol_id,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      );
  }, [allSelectableClaims, claimSearch, visibleClaims]);
  const qaScoreByClaim = useMemo(
    () =>
      new Map(
        qaScores
          .filter((score) => isRealClaimId(score.claim_id))
          .map((score) => [score.claim_id, score])
      ),
    [qaScores]
  );
  const qaVisibleClaims = useMemo(() => {
    return visibleClaims.map((claim) => {
      const score = qaScoreByClaim.get(claim.claim_id);
      return {
        ...claim,
        customer_name: claim.customer_name || score?.insured_name || '',
        quality_score: score?.quality_score,
        quality_percent: score?.quality_percent,
      };
    });
  }, [visibleClaims, qaScoreByClaim]);
  const requestedClaimId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const claimId = params.get('claimId') || location.state?.claimId || '';
    return isRealClaimId(claimId) ? claimId : '';
  }, [location.search, location.state]);
  const qaItemsClaimId =
    activeTab === 'quality-test'
      ? selectedQaClaimId || selectedClaimId
      : selectedClaimId || selectedQaClaimId;
  const getLocalQualityScoreValue = (claimId) => {
    if (!claimId) return '';

    const acceptedCount = Object.entries(acceptedReviews).filter(
      ([key, value]) => value && key.startsWith(`${claimId}::`)
    ).length;

    if (!acceptedCount) return '';

    const totalReviewItems =
      checkerUpdaterInsightFields.length + highlightedNextBestActionFields.length;
    return `${Math.min(100, Math.round((acceptedCount / totalReviewItems) * 100))}%`;
  };

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'quality-score') {
      setActiveTab('quality-test');
    } else if (tab === 'summary-insight' || tab === 'insights') {
      setActiveTab('summary-insight');
    } else if (tab === 'nba' || tab === 'insights-nba') {
      setActiveTab('nba');
    }
  }, [location.search]);

  useEffect(() => {
    saveStoredObject(REVIEW_EDITS_STORAGE_KEY, reviewEdits);
  }, [reviewEdits]);

  useEffect(() => {
    saveStoredObject(ACCEPTED_REVIEWS_STORAGE_KEY, acceptedReviews);
  }, [acceptedReviews]);

  useEffect(() => {
    let active = true;

    const loadClaims = async () => {
      try {
        const [claimResult, fnolResult] = await Promise.all([
          fetchFirstFnolJson('/claim-records'),
          fetchFirstFnolJson('/fnol-records').catch(() => ({ records: [] })),
        ]);

        if (active) {
          setClaims(mergeClaimRecords(claimResult.records || [], fnolResult.records || []));
          setLoadError('');
        }
      } catch (error) {
        if (active) setLoadError(error.message);
      }
    };

    loadClaims();
    const refresh = window.setInterval(loadClaims, 5000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadQaScores = async () => {
      try {
        const result = await fetchFirstFnolJson('/qa-scores');
        if (active) {
          setQaScores(result.records || []);
          setQaError('');
        }
      } catch (error) {
        if (active) setQaError(error.message);
      }
    };

    loadQaScores();
    const refresh = window.setInterval(loadQaScores, 5000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, []);

  useEffect(() => {
    if (!requestedClaimId) return;
    setHasManualClaimSelection(true);
    setSelectedClaimId((currentClaimId) =>
      currentClaimId === requestedClaimId ? currentClaimId : requestedClaimId
    );
    setSelectedQaClaimId((currentClaimId) =>
      currentClaimId === requestedClaimId ? currentClaimId : requestedClaimId
    );
  }, [requestedClaimId]);

  useEffect(() => {
    if (!selectedQaClaimId && qaVisibleClaims.length) {
      const preferredClaim =
        qaVisibleClaims.find((claim) => claim.claim_id === requestedClaimId) ||
        qaVisibleClaims[0];
      setSelectedQaClaimId(preferredClaim.claim_id);
    }
  }, [qaVisibleClaims, selectedQaClaimId, requestedClaimId]);

  useEffect(() => {
    if (!visibleClaims.length) return;

    const selectedVisibleClaim = allSelectableClaims.find((claim) => claim.claim_id === selectedClaimId);
    const isInsightOrNbaTab = activeTab === 'summary-insight' || activeTab === 'nba';
    const shouldPreferInsightClaim =
      isInsightOrNbaTab &&
      !requestedClaimId &&
      !hasManualClaimSelection &&
      (!selectedVisibleClaim || !selectedVisibleClaim.has_checker_insights);

    if (selectedClaimId && !selectedVisibleClaim) {
      setSelectedClaimId(visibleClaims[0].claim_id);
      return;
    }

    if (!selectedClaimId || shouldPreferInsightClaim) {
      const preferredClaim =
        visibleClaims.find((claim) => claim.claim_id === requestedClaimId) ||
        (isInsightOrNbaTab
          ? visibleClaims.find((claim) => claim.has_checker_insights)
          : null) ||
        visibleClaims.find((claim) => claim.has_checker_insights) ||
        visibleClaims[0];
      setSelectedClaimId(preferredClaim.claim_id);
    }
  }, [allSelectableClaims, visibleClaims, selectedClaimId, requestedClaimId, activeTab, hasManualClaimSelection]);

  useEffect(() => {
    if (!selectedClaimId) {
      setSelectedClaim({});
      return;
    }

    let active = true;
    setSelectedClaim({});
    setClaimDetailsLoading(true);

    const loadClaim = async () => {
      try {
        let result = null;
        let fnolResult = null;
        let claimSummaryResult = null;

        try {
          result = await fetchFirstFnolJson(
            `/claim-records/${encodeURIComponent(selectedClaimId)}`
          );
          claimSummaryResult = await fetchFirstFnolJson(
            `/claim-summary/${encodeURIComponent(selectedClaimId)}`
          ).catch(() => null);
        } catch (error) {
          const fnolId = getFnolIdFromClaim(selectedClaimId);
          if (!fnolId) throw new Error('Unable to load claim details');

          result = await fetchFirstFnolJson(
            `/fnol-records/${encodeURIComponent(fnolId)}`
          );
          fnolResult = result;
        }

        if (active) {
          const claimData = buildClaimDetailData(result, selectedClaimId);
          const fnolId = claimData.fnol_id || getFnolIdFromClaim(selectedClaimId);

          if (!fnolResult && fnolId) {
            try {
              fnolResult = await fetchFirstFnolJson(
                `/fnol-records/${encodeURIComponent(fnolId)}`
              );
            } catch {
              fnolResult = null;
            }
          }

          const fnolData = fnolResult ? buildClaimDetailData(fnolResult, selectedClaimId) : {};
          setSelectedClaim({
            ...claimData,
            fnol_loss_description: fnolData.loss_description || '',
            fnol_loss_location: fnolData.loss_location || '',
            loss_description: fnolData.loss_description || claimData.loss_description,
            loss_location: fnolData.loss_location || claimData.loss_location,
            claim_summary:
              claimSummaryResult?.claim_summary ||
              (!isDocumentLevelSummary(claimData.claim_summary) ? claimData.claim_summary : '') ||
              (!isDocumentLevelSummary(fnolData.claim_summary) ? fnolData.claim_summary : '') ||
              '',
          });
          setLoadError('');
        }
      } catch (error) {
        if (active) setLoadError(error.message);
      } finally {
        if (active) setClaimDetailsLoading(false);
      }
    };

    loadClaim();
    const refresh = window.setInterval(loadClaim, 5000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, [selectedClaimId]);

  useEffect(() => {
    if (!qaItemsClaimId) {
      setQaItems([]);
      return;
    }

    let active = true;

    const loadQaItems = async () => {
      try {
        const result = await fetchFirstFnolJson(
          `/qa-items/${encodeURIComponent(qaItemsClaimId)}`
        );
        if (active) {
          setQaItems(result.items || []);
          setQaError('');
        }
      } catch (error) {
        if (active) {
          setQaItems([]);
          setQaError(error.message);
        }
      }
    };

    loadQaItems();
    const refresh = window.setInterval(loadQaItems, 5000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, [qaItemsClaimId]);

  useEffect(() => {
    setEditingReview(null);
    setReviewDraft('');
    setOpenTaskMenuKey('');
    setSelectedDocumentObjectPath('');
    setSelectedDocumentSummaryKey('');
  }, [selectedClaimId]);

  useEffect(() => {
    if (!selectedClaimId) {
      setClaimDocuments([]);
      return;
    }

    let active = true;
    setDocumentsLoading(true);

    const loadDocuments = async () => {
      try {
        const result = await fetchFirstFnolJson(
          `/claim-documents/${encodeURIComponent(selectedClaimId)}`
        );
        if (active) {
          const documents = result.documents || [];
          setClaimDocuments(
            documents.length
              ? documents
              : await fetchProcessedClaimDocumentsFromGcs(selectedClaimId)
          );
        }
      } catch (error) {
        if (active) {
          const fallbackDocuments = await fetchProcessedClaimDocumentsFromGcs(selectedClaimId);
          setClaimDocuments(fallbackDocuments);
        }
      } finally {
        if (active) setDocumentsLoading(false);
      }
    };

    loadDocuments();
    const refresh = window.setInterval(loadDocuments, 5000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, [selectedClaimId]);

  const selectedClaimSummary =
    claims.find((claim) => claim.claim_id === selectedClaimId) || {};
  const claimDocumentsForDisplay = claimDocuments.filter(
    (document) => !isHiddenClaimDocument(document)
  );
  const selectedDocument = claimDocumentsForDisplay.find(
    (document) => document.object_path === selectedDocumentObjectPath
  );
  const selectedDocumentEffectiveSummaryKey =
    getSummaryAssistKeyForDocument(selectedDocument || {}) || selectedDocumentSummaryKey;

  const insuredName =
    selectedClaim.insured ||
    selectedClaim.customer_name ||
    selectedClaimSummary.customer_name ||
    '';
  const claimPickerOptions = useMemo(() => {
    if (
      selectedClaimId &&
      !searchedVisibleClaims.some((claim) => claim.claim_id === selectedClaimId)
    ) {
      return [
        {
          claim_id: selectedClaimId,
          customer_name: insuredName || selectedClaimSummary.customer_name || '',
        },
        ...searchedVisibleClaims,
      ];
    }

    return searchedVisibleClaims;
  }, [
    insuredName,
    searchedVisibleClaims,
    selectedClaimId,
    selectedClaimSummary.customer_name,
  ]);
  const selectedFnolId =
    selectedClaim.fnol_id ||
    selectedClaimSummary.fnol_id ||
    getFnolIdFromClaim(selectedClaimId);
  const rawLossSummary =
    selectedClaim.fnol_loss_description ||
    selectedClaim.loss_description ||
    selectedClaim.fnol_loss_location ||
    selectedClaim.loss_location ||
    '';
  const lossSummary =
    formatSingleHeaderLossSummary(rawLossSummary) ||
    'Loss summary will appear when claim details are available.';
  const claimSummaryInsight = splitInsight(
    getFieldValue(selectedClaim, [
      'claim_summary_checker_insights',
      'Claim_Summary_Agent_Insights',
      'Claim_Summary_Checker_Updater_Agent_Insights',
    ])
  ).summary;
  const claimSummary = 
    selectedClaim.claim_summary ||
    claimSummaryInsight ||
    'Claim summary will appear when claim insight is available.';
  const claimAssistContext = useMemo(
    () => ({
      claim_id: selectedClaimId,
      fnol_id: selectedFnolId,
      policy_number:
        selectedClaim.policy_number || selectedClaimSummary.policy_number || '',
      insured_name: insuredName,
      loss_summary: lossSummary,
      claim_summary: claimSummary,
    }),
    [
      selectedClaimId,
      selectedFnolId,
      selectedClaim.policy_number,
      selectedClaimSummary.policy_number,
      insuredName,
      lossSummary,
      claimSummary,
    ]
  );
  const selectClaim = (claimId) => {
    setHasManualClaimSelection(true);
    setSelectedClaimId(claimId);
    if (activeTab === 'quality-test') {
      setSelectedQaClaimId(claimId);
    }
  };
  const openClaimFromQa = (event, claimId) => {
    event.preventDefault();
    setHasManualClaimSelection(true);
    setSelectedClaimId(claimId);
    setSelectedQaClaimId(claimId);
    setActiveTab('quality-test');
    navigate(`/claims?tab=quality-score&claimId=${encodeURIComponent(claimId)}`);
  };
  const handleClaimSearch = (event) => {
    event.preventDefault();

    const query = claimSearch.trim();
    if (!query) return;

    const queryLower = query.toLowerCase();
    const matchedClaim =
      allSelectableClaims.find((claim) => claim.claim_id.toLowerCase() === queryLower) ||
      allSelectableClaims.find((claim) => claim.claim_id.toLowerCase().startsWith(queryLower)) ||
      searchedVisibleClaims[0];

    selectClaim(matchedClaim?.claim_id || query);
  };
  const summaryInsightFields = [
    ['Notice of Loss', 'Notice_Of_Loss_Agent_Insights'],
    ['Policy', ['Coverage_Agent_Insights']],
    ['Witness Statement', 'Witness_Reviewer_Agent_Insights'],
    ['Medical Record', 'Medical_Records_Reviewer_Agent_Insights'],
    ['Medical Bill', 'Bills_Invoices_Analyzer_Agent_Insights', buildBillsInvoicesInsight(selectedClaim)],
    ['Demand Letter', ['Demand_Letter_Agent_Insights']],
    ['Surveillance Photo', 'Surveillance_Security_Photo_Reviewer_Agent_Insights'],
    ['Surveillance Video', 'Surveillance_Security_Video_Reviewer_Agent_Insights'],
    ['Inspection Report', 'incident_report_agent_insights'],
    ['Damage Estimate Inventory', 'damage_estimate_inventory'],
    ['Investigation Report', 'incident_report_agent_insights'],
    ['Incident Report', 'incident_report_agent_insights'],
    ['Employee/Insured Statement', 'Insured_Reviewer_Agent_Insights'],
    ['Lease Agreement', 'Lease_Contract_Document_Reviewer_Agent_Insights'],
    ['Contract', 'Lease_Contract_Document_Reviewer_Agent_Insights'],
    ['Legal Document', 'Legal_Record_Reviewer_Agent_Insights'],
    ['Prior Claim', 'Prior_Claim_History_Loss_Report_Verifier_Agent_Insights'],
    ['Correspondence', 'External_Communicator_Agent_Insights'],
    ['Other Document', 'claim_summary_checker_insights'],
  ].map(([label, key, override]) => ({
    label,
    key: getFieldKey(key),
    matchKey: key,
    ...(override || splitInsight(getFieldValue(selectedClaim, key))),
  }));
  const matchedDocumentAssistSummaryInsightFields = selectedDocumentEffectiveSummaryKey
    ? summaryInsightFields.filter(({ matchKey }, index, fields) => (
        summaryFieldMatchesKey(matchKey, selectedDocumentEffectiveSummaryKey) &&
        fields.findIndex((field) => summaryFieldMatchesKey(field.matchKey, selectedDocumentEffectiveSummaryKey)) === index
      ))
    : [];
  const directSelectedDocumentInsight = splitInsight(
    getFieldValue(selectedClaim, selectedDocumentEffectiveSummaryKey)
  );
  const documentAssistSummaryInsightFields =
    matchedDocumentAssistSummaryInsightFields.length
      ? matchedDocumentAssistSummaryInsightFields
      : directSelectedDocumentInsight.summary
        ? [
            {
              label:
                documentSummaryLabelByKey[selectedDocumentEffectiveSummaryKey] ||
                cleanAgentHeadingLabel(
                  String(selectedDocumentEffectiveSummaryKey || '')
                    .replace(/_Agent_Insights$/i, '')
                    .replace(/_/g, ' ')
                ) ||
                'Document Summary',
              key: selectedDocumentEffectiveSummaryKey,
              summary: directSelectedDocumentInsight.summary,
              actions: directSelectedDocumentInsight.actions,
            },
          ]
        : [];
  const selectedClaimCreatedAt = getFieldValue(selectedClaim, [
    'Created_At',
    'created_at',
    'createdAt',
    'Updated_At',
    'updated_at',
    'processed_at',
  ]);
  const selectedClaimInsightNba = asObjectValue(selectedClaim.insight_nba);
  const selectedClaimInsightNbaInsights = asObjectValue(selectedClaimInsightNba.insights);
  const selectedClaimInsightNbaNextBestActions = asObjectValue(
    selectedClaimInsightNba.next_best_actions
  );
  const checkerUpdaterInsights = checkerUpdaterInsightFields
    .map(([label, key]) => {
      const insight = splitInsight(getFieldValue(selectedClaimInsightNbaInsights, key));
      const fieldKey = getFieldKey(key);
      const summary = prependCreatedAt(insight.summary, selectedClaimCreatedAt);
      const cleanLabel = cleanAgentHeadingLabel(label);

      return {
        label: cleanLabel,
        key: fieldKey,
        ...insight,
        summary,
      };
    });
  const consolidatedNbaSources = [
    getFieldValue(selectedClaimInsightNbaNextBestActions, consolidatedNbaFieldKeys),
    getFieldValue(selectedClaimInsightNba, consolidatedNbaFieldKeys),
    getFieldValue(selectedClaim, consolidatedNbaFieldKeys),
  ];
  const consolidatedNbaItems =
    consolidatedNbaSources
      .map(getConsolidatedNbaList)
      .find((items) => items.length > 0) ||
    buildConsolidatedNbaFromAgentFields(selectedClaimInsightNbaNextBestActions);
  const consolidatedNextActionItems = consolidatedNbaItems
    .map((item, index) => {
      const parsed = parseJsonStringValue(item);
      const label =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.label
          ? parsed.label
          : `Consolidated NBA ${index + 1}`;
      const action = formatConsolidatedNbaAction(item);

      return {
        id: `consolidated_nba_${index}`,
        label: cleanAgentHeadingLabel(label),
        action,
        reviewType: 'next-action-consolidated',
      };
    })
    .filter(({ action }) => hasRenderableNbaAction(action) && !isUnavailableNbaText(action));
  const nextActionRightPaneItems = consolidatedNextActionItems.length
    ? consolidatedNextActionItems
    : highlightedNextBestActionFields
        .map(([label, key]) => {
          const rawAction = getFieldValue(selectedClaimInsightNbaNextBestActions, key);
          const action = formatLatestNextBestAction(rawAction);

          return {
            id: getFieldKey(key),
            label: cleanAgentHeadingLabel(label),
            action,
            reviewType: 'next-action-highlight',
          };
        })
        .filter(({ action }) => hasRenderableNbaAction(action) && !isUnavailableNbaText(action));
  const documentInsightReviewItems = nextActionRightPaneItems;
  const reviewKey = (type, id) => `${selectedClaimId || 'no-claim'}::${type}::${id}`;
  const isEditing = (type, id) => editingReview?.type === type && editingReview?.id === id;
  const getReviewText = (type, id, fallback) =>
    isEditing(type, id) ? reviewEdits[reviewKey(type, id)] ?? fallback : fallback;
  const isAccepted = (type, id) => Boolean(acceptedReviews[reviewKey(type, id)]);
  const getQaItemForReview = (type, id) => {
    const expectedSourceType = type.startsWith('next-action') ? 'NBA' : 'INSIGHT';
    const expectedColumn = String(id || '').toLowerCase();

    return qaItems.find(
      (item) =>
        item.source_type === expectedSourceType &&
        String(item.source_column || '').toLowerCase() === expectedColumn
    );
  };
  const isForcedUncheckedReview = (type, id) =>
    selectedClaimId === 'C-260619-001' &&
    type === 'checker-summary' &&
    id === 'Coverage_Agent_Insights';
  const isReviewAccepted = (type, id) => {
    if (isForcedUncheckedReview(type, id)) return false;

    const qaItem = getQaItemForReview(type, id);
    return qaItem ? qaItem.check_status === 'Y' : isAccepted(type, id);
  };
  const isQaParameterRowAccepted = (item = {}) => {
    if (item.label === 'Timely Acknowledgment') return true;
    if (isReviewAccepted(item.reviewType, item.id)) return true;

    const mappedInsightLabels = qaParameterInsightMap[item.label] || [];
    return checkerUpdaterInsights.some(({ label, key }) =>
      mappedInsightLabels.includes(String(label).toLowerCase()) &&
      isReviewAccepted('checker-summary', key)
    );
  };
  const qaParameterItemsForDisplay = [...qaParameterNextBestActionItems].sort(
    (a, b) =>
      Number(isQaParameterRowAccepted(b)) - Number(isQaParameterRowAccepted(a))
  );
  const checkerUpdaterInsightsForDisplay = checkerUpdaterInsights
    .filter(({ summary }) => hasInsightContent(summary))
    .sort((a, b) =>
      Number(isReviewAccepted('checker-summary', a.key)) -
      Number(isReviewAccepted('checker-summary', b.key))
    );
  const documentInsightReviewItemsForDisplay = [...documentInsightReviewItems].sort((a, b) => {
    const aHasControls = a.reviewType === 'next-action-highlight';
    const bHasControls = b.reviewType === 'next-action-highlight';

    if (aHasControls !== bHasControls) return aHasControls ? -1 : 1;

    return Number(isReviewAccepted(a.reviewType, a.id)) -
      Number(isReviewAccepted(b.reviewType, b.id));
  });
  const toggleAcceptedReview = (type, id) => {
    const key = reviewKey(type, id);
    if (acceptedReviews[key]) return;

    setAcceptedReviews((current) => ({
      ...current,
      [key]: true,
    }));
    if (isEditing(type, id)) {
      setEditingReview(null);
      setReviewDraft('');
    }
  };
  const updateQaItemStatus = async (item) => {
    if (!item?.item_hash || qaUpdatingItemHash) return;

    const nextStatus = item.check_status === 'Y' ? 'PENDING' : 'Y';
    const previousItems = qaItems;
    setQaUpdatingItemHash(item.item_hash);
    setQaItems((current) =>
      current.map((candidate) =>
        candidate.item_hash === item.item_hash
          ? { ...candidate, check_status: nextStatus }
          : candidate
      )
    );

    try {
      const result = await fetchFirstFnolJson(
        `/qa-items/${encodeURIComponent(item.item_hash)}/status`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            check_status: nextStatus,
            checked_by: 'QA Reviewer',
          }),
        }
      );

      if (result.score) {
        setQaScores((current) => {
          const scoresByClaim = new Map(
            current.map((score) => [score.claim_id, score])
          );
          scoresByClaim.set(result.score.claim_id, result.score);
          return Array.from(scoresByClaim.values());
        });
      }
      setQaError('');
    } catch (error) {
      setQaItems(previousItems);
      setQaError(error.message);
    } finally {
      setQaUpdatingItemHash('');
    }
  };
  const startReviewEdit = (type, id, text) => {
    if (isAccepted(type, id)) return;
    setEditingReview({ type, id });
    setReviewDraft(text || '');
  };
  const getReviewEditorRows = (text = '') =>
    Math.max(
      4,
      String(text || '')
        .split('\n')
        .reduce((rows, line) => rows + Math.max(1, Math.ceil(line.length / 72)), 0)
    );
  const renderReviewEditor = (label) => (
    <textarea
      className="claim-review-editor"
      value={reviewDraft}
      rows={getReviewEditorRows(reviewDraft)}
      onChange={(event) => setReviewDraft(event.target.value)}
      aria-label={`Edit ${label}`}
    />
  );
  const saveReviewEdit = () => {
    if (!editingReview) return;
    setReviewEdits((current) => ({
      ...current,
      [reviewKey(editingReview.type, editingReview.id)]: reviewDraft,
    }));
    setEditingReview(null);
    setReviewDraft('');
  };
  const cancelReviewEdit = () => {
    setEditingReview(null);
    setReviewDraft('');
  };
  const renderReviewActions = (type, id, label, currentText) => {
    const qaItem = getQaItemForReview(type, id);
    const forcedUnchecked = isForcedUncheckedReview(type, id);
    const accepted = isReviewAccepted(type, id);
    const actionKey = reviewKey(type, id);
    const isNoteAction = type === 'checker-summary';
    const noteAdded = Boolean(noteAdditions[actionKey]);
    const assignedTo = taskAssignments[actionKey];
    const isTaskMenuOpen = openTaskMenuKey === actionKey;

    return (
      <div className="summary-review-actions" aria-label={`${label} review actions`}>
        <label className="summary-review-check">
          <input
            type="checkbox"
            checked={accepted}
            disabled={accepted || (qaItem && qaUpdatingItemHash === qaItem.item_hash)}
            onChange={() => {
              if (accepted) return;
              if (qaItem && !forcedUnchecked) {
                updateQaItemStatus(qaItem);
              } else {
                toggleAcceptedReview(type, id);
              }
            }}
            aria-label={`Accept ${label}`}
          />
        </label>
        {isEditing(type, id) ? (
          <>
            <button type="button" className="review-save-button" onClick={saveReviewEdit}>Save</button>
            <button type="button" className="review-cancel-button" onClick={cancelReviewEdit}>Cancel</button>
          </>
        ) : (
          <>
            {isNoteAction ? (
              <button
                type="button"
                className={`review-task-button ${noteAdded ? 'review-note-added' : ''}`}
                disabled={accepted || noteAdded}
                aria-disabled={accepted || noteAdded}
                onClick={() =>
                  setNoteAdditions((current) => ({
                    ...current,
                    [actionKey]: true,
                  }))
                }
              >
                {noteAdded ? 'Added' : 'Add to Note'}
              </button>
            ) : (
              <div className="review-task-menu">
                <button
                  type="button"
                  className="review-task-button"
                  disabled={accepted}
                  aria-disabled={accepted}
                  aria-expanded={isTaskMenuOpen}
                  onClick={() =>
                    setOpenTaskMenuKey((current) =>
                      current === actionKey ? '' : actionKey
                    )
                  }
                >
                  {assignedTo || 'Task'}
                </button>
                {isTaskMenuOpen && (
                  <div className="review-task-options" role="menu" aria-label={`Assign ${label}`}>
                    {taskAssignees.map((name) => (
                      <button
                        type="button"
                        key={name}
                        role="menuitem"
                        onClick={() => {
                          setTaskAssignments((current) => ({
                            ...current,
                            [actionKey]: name,
                          }));
                          setOpenTaskMenuKey('');
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              disabled={accepted}
              aria-disabled={accepted}
              onClick={() => startReviewEdit(type, id, currentText)}
            >
              Edit
            </button>
          </>
        )}
      </div>
    );
  };
  const renderNbaReviewActions = (type, id, label, currentText) => {
    const qaItem = getQaItemForReview(type, id);
    const forcedUnchecked = isForcedUncheckedReview(type, id);
    const accepted = isReviewAccepted(type, id);
    const actionKey = reviewKey(type, id);
    const assignedTo = taskAssignments[actionKey] || '';

    return (
      <div className="summary-review-actions nba-review-actions" aria-label={`${label} NBA actions`}>
        <label className="summary-review-check">
          <input
            type="checkbox"
            checked={accepted}
            disabled={accepted || (qaItem && qaUpdatingItemHash === qaItem.item_hash)}
            onChange={() => {
              if (accepted) return;
              if (qaItem && !forcedUnchecked) {
                updateQaItemStatus(qaItem);
              } else {
                toggleAcceptedReview(type, id);
              }
            }}
            aria-label={`Accept ${label}`}
          />
        </label>
        {isEditing(type, id) ? (
          <>
            <button type="button" className="review-save-button" onClick={saveReviewEdit}>Save</button>
            <button type="button" className="review-cancel-button" onClick={cancelReviewEdit}>Cancel</button>
          </>
        ) : (
          <>
            <select
              className="nba-task-select"
              value={assignedTo}
              disabled={accepted}
              aria-label={`Assign ${label}`}
              onChange={(event) => {
                const value = event.target.value;
                setTaskAssignments((current) => {
                  const next = { ...current };
                  if (value) {
                    next[actionKey] = value;
                  } else {
                    delete next[actionKey];
                  }
                  return next;
                });
              }}
            >
              <option value="">Task</option>
              {taskAssignees.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={accepted}
              aria-disabled={accepted}
              onClick={() => startReviewEdit(type, id, currentText)}
            >
              Edit
            </button>
          </>
        )}
      </div>
    );
  };
  const renderNbaActionCopy = (currentAction = '') => {
    const text = String(currentAction || '').trim();
    const nbaSections = [
      ['Action', getNbaFieldValue(text, 'Action')],
      ['Reason', getNbaFieldValue(text, 'Reason')],
      ['Resolves', getNbaFieldValue(text, 'Resolves')],
    ].filter(([, value]) => value);

    if (!nbaSections.length) {
      return <p style={{ whiteSpace: 'pre-line' }}>{text}</p>;
    }

    return (
      <div className="nba-action-copy-lines">
        {nbaSections.map(([heading, value]) => (
          <div
            className={`nba-action-copy-line nba-action-copy-line-${heading.toLowerCase()}`}
            key={heading}
          >
            <span>{heading}</span>
            <p>{value}</p>
          </div>
        ))}
      </div>
    );
  };
  const insightStandaloneHeadings = new Set([
    'assessment',
    'confidence score',
    'key factors',
    'evidence used',
    'conflicting facts',
    'exposure risk',
    'rationale',
    'financial impact',
    'injury / diagnosis',
    'treatment',
    'property / repair damage',
    'cumulative documented damages',
    'documented specials',
    'pain, impairment, and non-economic loss',
  ]);
  const renderInsightBody = (currentBody = '') => {
    const lines = String(currentBody || '').split('\n');

    return (
      <p className="colored-insight-copy">
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          const colonMatch = line.match(/^([^:\n]{2,80})(:\s*)(.*)$/);
          const isStandaloneHeading = insightStandaloneHeadings.has(trimmedLine.toLowerCase());

          return (
            <React.Fragment key={`${trimmedLine}-${index}`}>
              {colonMatch ? (
                <>
                  <span className="insight-field-heading">{colonMatch[1]}{colonMatch[2]}</span>
                  <span className="insight-field-value">{colonMatch[3]}</span>
                </>
              ) : isStandaloneHeading ? (
                <span className="insight-field-heading insight-field-heading-line">{line}</span>
              ) : (
                <span className="insight-field-value">{line}</span>
              )}
              {index < lines.length - 1 ? <br /> : null}
            </React.Fragment>
          );
        })}
      </p>
    );
  };
  const renderQaReviewItems = () => {
    return (
      <div className="qa-parameter-table-shell">
        <table className="qa-parameter-table">
          <thead>
            <tr>
              <th>QA Parameter</th>
              <th>Description</th>
              <th>Criticality</th>
            </tr>
          </thead>
          <tbody>
            {qaParameterItemsForDisplay.map((item) => {
              const accepted = isQaParameterRowAccepted(item);
              return (
                <tr
                  key={item.id}
                  className={accepted ? 'qa-parameter-row-accepted' : ''}
                >
                  <td>{item.label}</td>
                  <td>{item.description}</td>
                  <td>
                    <span className={`qa-criticality-badge qa-criticality-${item.criticality.toLowerCase()}`}>
                      {item.criticality}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  const renderTabContent = () => {
    if (!selectedClaimId) {
      return (
        <div className="claim-tab-empty">
          {loadError || 'No claim selected.'}
        </div>
      );
    }

    if (activeTab === 'correspondence') {
      return (
        <section className="claim-tab-card inquiry-document-split-card merged-inquiry-summary-card">
          <div className="inquiry-document-split inquiry-document-summary-split">
            <div className="inquiry-document-pane inquiry-document-left-pane" aria-label="Inquiry">
              <div className="inquiry-document-heading-row">
                <span>Name</span>
                <span>Date &amp; Time</span>
                <span>Flag</span>
                <span>Remark</span>
                <span>Attachment</span>
              </div>
              <div className="inquiry-document-table-body">
                {documentsLoading ? (
                  <div className="claim-tab-empty">Loading claim documents...</div>
                ) : claimDocumentsForDisplay.length ? (
                  claimDocumentsForDisplay.map((document) => {
                    const fraudFlag = getDocumentFraudFlag(document);
                    const fraudRemark = getDocumentFraudRemark(document);
                    const documentDate = formatDocumentDate(
                      getDocumentTimestamp(document)
                    );
                    const summaryAssistKey = getSummaryAssistKeyForDocument(document);
                    const isSelectedDocument =
                      selectedDocumentObjectPath === document.object_path;

                    return (
                      <div
                        key={document.object_path}
                        className={`inquiry-document-row${
                          document.missing ? ' inquiry-document-row-missing' : ''
                        }${isSelectedDocument ? ' inquiry-document-row-selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedDocumentObjectPath(document.object_path);
                          setSelectedDocumentSummaryKey(summaryAssistKey);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedDocumentObjectPath(document.object_path);
                            setSelectedDocumentSummaryKey(summaryAssistKey);
                          }
                        }}
                      >
                        <span title={document.name}>{document.name}</span>
                        <span className="document-date-time">{documentDate}</span>
                        <span>
                          <span
                            className={`document-fraud-flag${
                              fraudFlag.tone ? ` document-fraud-flag-${fraudFlag.tone}` : ''
                            }`}
                            title={fraudFlag.label}
                            aria-label={fraudFlag.label}
                          />
                        </span>
                        <span title={fraudRemark}>{fraudRemark}</span>
                        <span className="document-attachment-cell">
                          {!document.missing && (
                            <a
                              className="inquiry-document-row-open"
                              href={buildClaimDocumentContentUrl(
                                selectedClaimId,
                                document.object_path
                              )}
                              target="_blank"
                              rel="noreferrer"
                              title={`Open ${document.name}`}
                              aria-label={`Open ${document.name}`}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <i className="fas fa-paperclip" aria-hidden="true"></i>
                            </a>
                          )}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="claim-tab-empty">
                    No processed documents are available for this claim yet.
                  </div>
                )}
              </div>
            </div>
            <div className="inquiry-document-pane inquiry-document-summary-pane" aria-label="Document Summary">
              <div className="inquiry-document-summary-header">
                <span>Document Summary</span>
              </div>
              <div className="claims-ai-content claim-insight-list inquiry-document-summary-list">
                {claimDetailsLoading ? (
                  <div className="claim-tab-empty">Loading Document Summary...</div>
                ) : !selectedDocumentObjectPath ? (
                  null
                ) : documentAssistSummaryInsightFields.length ? (
                  documentAssistSummaryInsightFields.map(({ label, key, summary }) => {
                    const latestSummary = formatLatestDocumentSummary(summary);

                    return (
                      <article className="readable-insight-card document-summary-card" key={key}>
                        <div className="summary-review-copy">
                          <h2>{label}</h2>
                          <p style={{ whiteSpace: 'pre-line' }}>{latestSummary || noInsightPlaceholder}</p>
                        </div>
                      </article>
                    );
                  })
                ) : (
                  <div className="claim-tab-empty">
                    No matching Document Summary is available for this document yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      );
    }

    if (activeTab === 'summary-insight') {
      const expandedInsight = checkerUpdaterInsightsForDisplay.find(
        ({ key }) => key === expandedInsightKey
      );
      const renderInsightBlocks = (blocks, key, label) => {
        if (blocks.length <= 1 && !blocks[0]?.createdAt) {
          const reviewId = `${key}::history::0`;
          const currentBody = getReviewText(
            'checker-summary',
            reviewId,
            blocks[0]?.body || noInsightPlaceholder
          );

          return (
            <div className="created-at-insight-block single-created-at-insight-block">
              <div className="historical-insight-content">
                {isEditing('checker-summary', reviewId) ? (
                  renderReviewEditor(`${label} insight`)
                ) : (
                  renderInsightBody(currentBody)
                )}
              </div>
              {renderReviewActions('checker-summary', reviewId, label, currentBody)}
            </div>
          );
        }

        const displayBlocks = blocks
          .map((block, index) => ({
            ...block,
            originalIndex: index,
            reviewId: `${key}::history::${index}`,
          }))
          .sort(
            (firstBlock, secondBlock) =>
              Number(isReviewAccepted('checker-summary', firstBlock.reviewId)) -
                Number(isReviewAccepted('checker-summary', secondBlock.reviewId)) ||
              firstBlock.originalIndex - secondBlock.originalIndex
          );

        return (
          <div className="created-at-insight-blocks multi-created-at-insight-blocks">
            {displayBlocks.map(({ createdAt, body, originalIndex, reviewId }) => {
              const accepted = isReviewAccepted('checker-summary', reviewId);
              const currentBody = getReviewText(
                'checker-summary',
                reviewId,
                body || noInsightPlaceholder
              );

              return (
                <div
                  className={`created-at-insight-block ${originalIndex === 0 ? 'latest-created-at-insight' : 'previous-created-at-insight'} ${accepted ? 'created-at-insight-block-accepted' : ''}`}
                  key={`${key}-created-at-${originalIndex}`}
                >
                  <div className="historical-insight-content">
                    {createdAt ? (
                      <div className="created-at-insight-label">{createdAt}</div>
                    ) : null}
                    {isEditing('checker-summary', reviewId) ? (
                      renderReviewEditor(`${label} insight ${originalIndex + 1}`)
                    ) : (
                      renderInsightBody(currentBody)
                    )}
                  </div>
                  {renderReviewActions('checker-summary', reviewId, label, currentBody)}
                </div>
              );
            })}
          </div>
        );
      };

      return (
        <section className="claim-tab-card summary-insight-split-card summary-insight-only-card insight-tile-stage">
          <div className="summary-insight-split">
            <div className="summary-insight-pane summary-insight-left-pane" aria-label="Featured insights">
              <div className="summary-insight-left-scroll">
                <div className="claims-ai-content claim-insight-list insight-tile-grid">
                  {claimDetailsLoading ? (
                    <div className="claim-tab-empty">Loading featured insights...</div>
                  ) : checkerUpdaterInsightsForDisplay.length ? (
                    checkerUpdaterInsightsForDisplay.map(({ label, key, summary }) => {
                      const currentSummary = getReviewText(
                        'checker-summary',
                        key,
                        summary || noInsightPlaceholder
                      );
                      const insightBlocks = splitCreatedAtInsightBlocks(currentSummary);
                      const latestInsight = insightBlocks[0] || { body: currentSummary };

                      return (
                        <article
                          role="button"
                          tabIndex={0}
                          className={`summary-insight-tile ${isReviewAccepted('checker-summary', key) ? 'summary-insight-tile-accepted' : ''}`}
                          key={key}
                          onClick={() => setExpandedInsightKey(key)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setExpandedInsightKey(key);
                            }
                          }}
                          aria-haspopup="dialog"
                        >
                          <span className="summary-insight-tile-prompt">
                            <i className={getInsightTileIconClass(label)} aria-hidden="true"></i>
                            <span>{label}</span>
                          </span>
                          <div className="summary-insight-tile-latest">
                            {latestInsight.createdAt ? (
                              <span>{latestInsight.createdAt}</span>
                            ) : null}
                            {renderInsightBody(latestInsight.body || noInsightPlaceholder)}
                          </div>
                        </article>
                      );
                    })
                  ) : (
                    <div className="claim-tab-empty">
                      No featured insights are available for this claim yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {expandedInsight ? (() => {
            const { label, key, summary } = expandedInsight;
            const currentSummary = getReviewText(
              'checker-summary',
              key,
              summary || noInsightPlaceholder
            );
            const insightBlocks = splitCreatedAtInsightBlocks(currentSummary);

            return (
              <div
                className="summary-insight-fullscreen"
                role="dialog"
                aria-modal="true"
                aria-label={`${label} insights`}
                onClick={() => setExpandedInsightKey('')}
              >
                <article
                  className={`summary-insight-fullscreen-card ${isReviewAccepted('checker-summary', key) ? 'summary-insight-fullscreen-card-accepted' : ''}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <header className="summary-insight-fullscreen-header">
                    <div>
                      <span>Insight</span>
                      <h2>{label}</h2>
                    </div>
                    <button
                      type="button"
                      className="summary-insight-close"
                      aria-label={`Close ${label} insights`}
                      onClick={() => setExpandedInsightKey('')}
                    >
                      <i className="fas fa-xmark" aria-hidden="true"></i>
                    </button>
                  </header>
                  <div className="summary-insight-fullscreen-body">
                    {renderInsightBlocks(insightBlocks, key, label)}
                  </div>
                </article>
              </div>
            );
          })() : null}
        </section>
      );
    }

    if (activeTab === 'nba') {
      const visibleNbaReviewItems = documentInsightReviewItemsForDisplay.filter(
        ({ id, action, reviewType }) => {
          const currentAction = getReviewText(reviewType, id, action);
          return hasRenderableNbaAction(currentAction) && !isUnavailableNbaText(currentAction);
        }
      );

      return (
        <section className="claim-tab-card summary-insight-split-card nba-only-card">
          <div
            className="summary-insight-pane summary-insight-right-pane nba-full-pane"
            aria-label="NBA reviews"
            ref={nbaFullPaneRef}
          >
            <div className="claims-ai-content next-action-list nba-tile-grid">
              {claimDetailsLoading ? (
                <div className="claim-tab-empty">Loading NBA...</div>
              ) : visibleNbaReviewItems.length ? (
                visibleNbaReviewItems.map(({ id, label, action, reviewType }) => {
                  const hasReviewControls = true;
                  const currentAction = getReviewText(reviewType, id, action);
                  return (
                    <article
                      className={`nba-action-tile ${hasReviewControls && isReviewAccepted(reviewType, id) ? 'nba-action-tile-accepted' : ''}`}
                      key={`${reviewType}-${id}`}
                    >
                      <div className="nba-action-tile-controls">
                        {hasReviewControls && renderNbaReviewActions(reviewType, id, label, currentAction)}
                      </div>
                      <div className="summary-review-copy next-action-control-copy">
                        {hasReviewControls && isEditing(reviewType, id) ? (
                          renderReviewEditor(label)
                        ) : (
                          renderNbaActionCopy(currentAction)
                        )}
                      </div>
                    </article>
                  );
                })
              ) : (
                <div className="claim-tab-empty">No NBA is available for this claim yet.</div>
              )}
            </div>
          </div>
        </section>
      );
    }

    if (activeTab === 'agent-assist') {
      return (
        <section className="claim-tab-card claim-agent-assist-tab">
          <h2 className="visually-hidden-layout">Agent Assist</h2>
          <ChatAgent
            agent={claimAssistAgent}
            context={claimAssistContext}
            hideTitle
            sessionKey={selectedClaimId || 'no-claim'}
          />
        </section>
      );
    }

    if (activeTab === 'quality-test') {
      return (
        <section className="claim-tab-card claim-quality-score-blank" aria-label="Quality score"></section>
      );
    }

    if (activeTab === 'next-best-action') {
      return (
        <section className="claim-tab-card summary-insight-split-card document-insights-split-card">
          <div className="summary-insight-pane summary-insight-right-pane document-insights-full-pane" aria-label="Document Summary">
            <div className="claims-ai-content claim-insight-list">
              {claimDetailsLoading ? (
                <div className="claim-tab-empty">Loading Document Summary...</div>
              ) : summaryInsightFields.length ? (
                summaryInsightFields.map(({ label, key, summary }) => (
                  <article className="readable-insight-card document-summary-card" key={key}>
                    <div className="summary-review-copy">
                      <h2>{label}</h2>
                      <p style={{ whiteSpace: 'pre-line' }}>{summary || noInsightPlaceholder}</p>
                    </div>
                  </article>
                ))
              ) : (
                <div className="claim-tab-empty">
                  No Document Summary is available for this claim yet.
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    return (
      <section className="claim-tab-card">
        {claimSections.map((section) => (
          <React.Fragment key={section.title}>
            <ClaimsFieldSection
              title={section.title}
              fields={section.fields}
              type={section.type}
              data={selectedClaim}
            />
            {section.title === 'Claimant Information' && (
              <ClaimsStandaloneField
                label="Loss Type"
                value={getFieldValue(selectedClaim, ['loss_type', 'type_of_loss', 'lossType', 'possible_loss_type', 'loss_category', 'category'])}
                showIfEmpty={Boolean(selectedClaimId)}
              />
            )}
          </React.Fragment>
        ))}
      </section>
    );
  };

  return (
    <div className="dashboard-page claims-form-page">
      <Header onLogout={onLogout} />

      <div className="page-body">
        <aside className="sidebar mini-sidebar">
          <nav className="sidebar-nav">
            <button onClick={() => navigate('/home')} className="nav-item">
              <i className="fas fa-home"></i><span className="nav-label">Home</span>
            </button>
            <button onClick={() => navigate('/my-work')} className="nav-item">
              <i className="fas fa-briefcase"></i><span className="nav-label">My Work</span>
            </button>
            <button onClick={() => navigate('/crm')} className="nav-item">
              <i className="fas fa-address-book"></i><span className="nav-label">FNOL</span>
            </button>
            <button
              onClick={() => {
                setActiveTab('claims-info');
                navigate('/claims');
              }}
              className={`nav-item ${activeTab !== 'quality-test' ? 'active' : ''}`}
            >
              <i className="fas fa-file-shield"></i><span className="nav-label">Claim</span>
            </button>
            <button onClick={() => navigate('/ops-dashboard')} className="nav-item">
              <i className="fas fa-tachometer-alt"></i><span className="nav-label">Ops Dashboard</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('quality-test')}
              className={`nav-item quality-score-nav-item ${activeTab === 'quality-test' ? 'active' : ''}`}
            >
              <i className="fas fa-clipboard-check"></i><span className="nav-label">QA</span>
            </button>
            <button onClick={() => navigate('/agent-dashboard')} className="nav-item">
              <i className="fas fa-gauge-high"></i><span className="nav-label">AI Agent Dashboard</span>
            </button>
            <button onClick={openDocumentUploadBucket} className="nav-item">
              <i className="fas fa-file-arrow-up"></i><span className="nav-label">Document Upload</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <button onClick={onLogout} className="nav-item logout-btn">
              <i className="fas fa-sign-out-alt"></i><span className="nav-label">Logout</span>
            </button>
          </div>
        </aside>

        <main className="db-content claims-form-main claim-work-main" aria-label="Claims">
          <section className={`claim-workspace ${activeTab === 'quality-test' ? 'claim-workspace-blank' : ''}`}>
            {activeTab === 'quality-test' ? (
              <div className="quality-score-split-layout">
                <section className="quality-score-table-shell" aria-label="Quality score claims">
                  <table className="quality-score-table">
                    <thead>
                      <tr>
                        <th>Claim ID</th>
                        <th>Quality Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qaVisibleClaims.length > 0 ? (
                        qaVisibleClaims.map((claim) => {
                          const score = qaScoreByClaim.get(claim.claim_id);
                          const scoreValue = score
                            ? formatQualityScoreValue(score)
                            : getLocalQualityScoreValue(claim.claim_id) ||
                              formatFallbackQualityScore(claim);
                          return (
                          <tr
                            key={claim.claim_id}
                            className={selectedQaClaimId === claim.claim_id ? 'selected-quality-row' : ''}
                            onClick={() => {
                              setSelectedQaClaimId(claim.claim_id);
                              setSelectedClaimId(claim.claim_id);
                            }}
                          >
                            <td className="quality-claim-number">
                              <a
                                href={`/claims?tab=quality-score&claimId=${encodeURIComponent(claim.claim_id)}`}
                                className="quality-claim-link"
                                onClick={(event) => openClaimFromQa(event, claim.claim_id)}
                              >
                                {claim.claim_id}
                              </a>
                            </td>
                            <td>
                              <span className={`quality-score-badge ${getQualityScoreClass(scoreValue)}`}>
                                {scoreValue}
                              </span>
                            </td>
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td className="quality-score-empty-row" colSpan="2">
                            {qaError || loadError || 'No claim records yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </section>
                <section className="quality-score-detail-pane" aria-label="QA details">
                  {renderQaReviewItems()}
                </section>
              </div>
            ) : (
              <>
                <div className="claim-top-row">
                  <section className="loss-summary-card">
                    <div className="claim-identity-banner">
                      <strong><span>Claim No:</span> {selectedClaimId || 'XXXXXX'}</strong>
                      <strong><span>FNOL ID:</span> {selectedFnolId || 'XXXXX'}</strong>
                      <strong><span>Insured Name:</span> {insuredName || 'XXXXX'}</strong>
                    </div>
                    <div className="loss-summary-row">
                      <h2>Loss summary</h2>
                      <p>{lossSummary}</p>
                    </div>
                    <div className="loss-summary-row">
                      <h2>Claim summary</h2>
                      <p>{claimSummary}</p>
                    </div>
                  </section>
                  <form className="claim-picker" onSubmit={handleClaimSearch}>
                    <span>Claim</span>
                    <input
                      className="claim-search-input"
                      type="search"
                      value={claimSearch}
                      onChange={(event) => setClaimSearch(event.target.value)}
                      placeholder="Search claim ID"
                      aria-label="Search claim ID"
                    />
                    <button
                      className="claim-search-button"
                      type="submit"
                      aria-label="Search claim"
                      title="Search claim"
                    >
                      <i className="fas fa-search" aria-hidden="true"></i>
                    </button>
                    <select
                      value={selectedClaimId}
                      onChange={(event) => selectClaim(event.target.value)}
                      aria-label="Select claim"
                    >
                      <option value="">Select claim</option>
                      {claimPickerOptions.map((claim) => (
                        <option value={claim.claim_id} key={claim.claim_id}>
                          {getClaimOptionLabel(claim)}
                        </option>
                      ))}
                    </select>
                  </form>
                </div>

                <div className="claim-body-grid">
                  <div className="claim-left-column">
                    <nav className="claim-tab-nav" aria-label="Claim sections">
                      <button
                        type="button"
                        className={activeTab === 'claims-info' ? 'active' : ''}
                        onClick={() => setActiveTab('claims-info')}
                      >
                        Claims Info
                      </button>
                      <button
                        type="button"
                        className={activeTab === 'correspondence' ? 'active' : ''}
                        onClick={() => setActiveTab('correspondence')}
                      >
                        <span className="tab-ai-agent-head" aria-hidden="true">
                          <i className="fas fa-robot"></i>
                        </span>
                        <span>Document Assist</span>
                      </button>
                      <button
                        type="button"
                        className={activeTab === 'summary-insight' ? 'active' : ''}
                        onClick={() => setActiveTab('summary-insight')}
                      >
                        <span className="tab-ai-agent-head" aria-hidden="true">
                          <i className="fas fa-robot"></i>
                        </span>
                        <span>Insights</span>
                      </button>
                      <button
                        type="button"
                        className={activeTab === 'nba' ? 'active' : ''}
                        onClick={() => setActiveTab('nba')}
                      >
                        <span className="tab-ai-agent-head" aria-hidden="true">
                          <i className="fas fa-robot"></i>
                        </span>
                        <span>NBA</span>
                      </button>
                      <button
                        type="button"
                        className={activeTab === 'agent-assist' ? 'active' : ''}
                        onClick={() => setActiveTab('agent-assist')}
                      >
                        <span className="tab-ai-agent-head" aria-hidden="true">
                          <i className="fas fa-robot"></i>
                        </span>
                        <span>Agent Assist</span>
                      </button>
                    </nav>

                  <div className={`claim-tab-content ${activeTab === 'agent-assist' ? 'claim-tab-content-agent-assist' : ''}`}>
                    {renderTabContent()}
                  </div>
                  </div>
                </div>
              </>
            )}
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default Claims;
